import React, { useState, useEffect, useMemo, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Box, Stack, Tabs, Tab, IconButton, Divider } from "@mui/material";
import {
	AddOutlined,
	CloseOutlined,
} from "@mui/icons-material";
import CustomLayout from "../../../components/layout";
import PrimaryButton from "../../../components/buttons/primary-button";
import SecondaryButton from "../../../components/buttons/secondary-button";
import LoanInfoCard from "../../../components/info-card";
import QualifyAccordion from "../../../components/qualify-accordion";
import CustomAlert from "../../../components/custom-alert";
import CustomCheckbox from "../../../components/custom-checkbox";
import CustomRadio from "../../../components/custom-radio";
import RequiredError from "../../../components/required-error";
import ConfirmationModal from "../../../components/modal/confirmation-modal";
import ProcessingModal from "../../../components/modal/processing-modal";
import CommanErrorModal from "../../../components/modal/common-error-modal";
import Heading4 from "../../../components/typography/heading-04";
import Body2 from "../../../components/typography/body-02";
import Body3 from "../../../components/typography/body-03";
import OutlinedTextInput from "../../../components/custom-input/outlined-text-input";
import MaskedInput from "../../../components/custom-input/masked-input";
import SSNMaskedInput from "../../../components/custom-input/ssn-masked-input";
import SecondarySelect from "../../../components/custom-select/secondary-select";
import {
	setCurrentStage,
	selectCurrentStage,
	setSectionStatus,
	syncSectionStatus,
	setLoanGuid,
	selectLoanGuid,
	selectAuraResult,
	selectApplicationDataChanged,
	selectSectionStatuses,
	selectXmlApplicationData,
	clearXmlApplicationData,
	selectIncomeDocumentationType,
	setIncomeDocumentationType,
	resetQualifyProgress,
	selectAuraRunAt,
	selectSelectedProgram,
	selectLockDecision,
	selectSelectedPath,
	hydrateProgress,
	markProgressHydrated,
	selectLoanOriginatorId as selectPersistedLoanOriginatorId,
	selectLoanProcessorId as selectPersistedLoanProcessorId,
	setLoanOriginatorId as setPersistedLoanOriginatorId,
	setLoanProcessorId as setPersistedLoanProcessorId,
	markLiabilitiesChanged,
	selectLiabilitiesChangedSinceAura,
	setDashboardDirty,
	selectDashboardSaveRequest,
	setDashboardSaveRequest,
} from "../../../features/current-loan/currentLoanSlice";
import { useSyncAuraProgress } from "../../../features/current-loan/useSyncAuraProgress";
import { isDSCRstyle } from "../../../utils/income-documentation-helpers";
import { INCOME_VERIFICATION_TYPES } from "../../../utils/quickPricerConstants";
import { getQualifyEntryPath } from "../../../utils/qualify-entry-path";
import {
	selectLoEmail,
	selectLpEmail,
	selectUserDetails,
	selectSelectedOrganization,
	selectOrganizationName,
	selectChildOrganizationName,
} from "../../../features/auth/authSlice";
import { selectImpersonatedUser } from "../../../features/admin/impersonated-user/impersonatedUserSlice";
import {
	selectedUserWithLoanChannel,
	selectedUser,
	resolveBrokerFromList,
	isInvalidLoLpName,
	buildLoLpFullName,
} from "../../../utils/common-helpers";
import { normalizeXmlForCreateLoan, isRealLoanGuid } from "../../../utils/normalize-xml-for-create-loan";
import { useCreateLoanMutation } from "../../../features/create-loan/createLoanApiSlice";
import { LOAN_CHANNEL_CONSTANTS, BROKER_ADMIN, TPO_SECONDARY } from "../../../utils/common-constants";
import { useGetLoanMutation } from "../../../features/get-loan-data/getLoanApiSlice";
import { useGetBrokerListByTitleBranchMutation } from "../../../features/broker-search-title-branch/brokerSearchTitleBranchApiSlice";
import { useGetBorrowerPairMutation } from "../../../features/get-borrower-pair/getBorrowerPairApiSlice";
import SecondarySelectLOLP from "../../../components/custom-select/secondary-select-lo-lp";
import LoanOriginator from "../../../components/loan-form-card/loan-originator";
import LoanProcessor from "../../../components/loan-form-card/loan-processor";
import { useGetOrganizationDetailsMutation } from "../../../features/organization-detail/organizationDetailApiSlice";
import { useDeleteBorrowerPairMutation } from "../../../features/delete-borrower-pair/deleteBorrowerPairApiSlice";
import { useLazyGetCurrentLoanProgressQuery } from "../../../features/current-loan/currentLoanApiSlice";
import { useUpdateBorrowerPairMutation } from "../../../features/update-borrower-pair/updateBorrowerPairApiSlice";
import { useCreateBorrowerPairMutation } from "../../../features/create-borrower-pair/createBorrowerPairApiSlice";
import { useUpdateFieldsMutation } from "../../../features/update-fields/updateFieldsApiSlice";
import { useUpdateLoanMutation } from "../../../features/update-loan/updateLoanApiSlice";
import { useFieldMutation } from "../../../features/field-values/fieldValueApiSlice";
import { useDeleteVoxMutation } from "../../../features/delete-vox/deleteVoxApiSlice";
import { useDeleteRealEstateMutation } from "../../../features/delete-real-estate-property/delete-real-estate-api-slice";
import { useFetchAEEmailsMutation } from "../../../features/fetch-ae-emails/fetchAEEmailsApiSlice";
import { useGetLoanDetailMutation } from "../../../features/loan-detail-v3/loan-detail-v3-api-slice";
import {
	buildPersonPayload,
	buildResidencesPayload,
	buildEmploymentPayload,
	buildAssetsPayload,
	buildSubjectPropertyLoanData,
	mapPersonFromReal,
	mapResidencesFromReal,
	mapEmploymentFromReal,
	mapAssetsFromReal,
	mapSubjectPropertyFromLoan,
	emptyDeclarations,
	emptyAssets,
	defaultEmploymentRows,
	newEmploymentRow,
	isSelfEmployedRow,
	reoLinkKey,
	toBool,
	resolveClosingVesting,
	applyMailingSameAsCurrent,
	newAssetRow,
	removeUnsavedAssetRow,
	reshiftAfterAssetDelete,
	resolveAssetDeleteIds,
	ASSET_LIST_META,
	MAX_ASSET_ROWS,
	LIABILITY_SOURCE,
	liabilitySource,
	stagedLiabilityRemovals,
	resolveLiabilityRemovalIds,
	toWizardDate,
} from "../../../utils/current-loan-encompass-mappers";
import LiabilitiesReview, { LiabilityRowTags, linkedRemovalText } from "./components/liabilities-review";
import TemporaryDrawer from "../../../components/temporary-drawer";
import {
	buildTpoLoanData,
	buildOriginatorInfoData,
	buildLoanContactFieldValues,
	buildAeEmailFieldValues,
	hasAeEmail,
} from "../../../utils/loan-tpo-payload";
import { toggleDemographic, setAmericanIndianTribe } from "../../../utils/demographic-handlers";
import {
	CLOSING_FIELD_IDS,
	CLOSING_FEE_MIRROR_FIELD_IDS,
	CLOSING_FEE_DESCRIPTION_FIELDS,
	CLOSING_FEE_PAID_TO_FIELD,
	CLOSING_LOCKED_FIELD_KEYS,
	CLOSING_VESTING_FIELD_IDS,
	CLOSING_READ_ONLY_FIELD_IDS,
	INDIVIDUAL_VESTING_METHODS,
	ENTITY_VESTING_METHODS,
	PROPERTY_FIELD_IDS,
	ADDITIONAL_CONTACT_FIELD_IDS,
} from "../../../utils/current-loan-encompass-field-ids";
import {
	FOREIGN_NATIONAL_FIELD_IDS,
	buildForeignNationalFieldValues,
	foreignNationalSlotsFromFields,
} from "../../../utils/foreign-national-fields";
import {
	maritalStatusOptions,
	citizenshipTypeOptions,
	countryOptions,
	legalSpouseOptions,
	relationshipTypeOptions,
	stateOptions,
	housingExpenseOptions,
	creditReissueOptions,
	ownershipShareOptions,
	ownerOptions,
	incomeOwnerOptions,
	additionalIncomeSourceOptions,
	assetTypeOptions,
	otherAssetsTypeOptions,
	otherAssetsDescriptionOptions,
	otherLiabilitiesTypeOptions,
	liabilityTypeOptions,
	realEstateStatusOptions,
	propertyTypeOptions,
	propertyJointOptions,
	YNOptions,
	vestingMethodOptions,
	lpcValueOptions,
	bpcValueOptions,
	realEstateUseOptions,
	lienPositionOptions,
} from "../../../utils/select-options";
import { VERBIAGE_CONSTANTS } from "../../../utils/verbiage.constants";
import { useErrorMessages } from "../../../utils/error-messages.constants";
import { getConflictPopup, isConflictError } from "../../../utils/conflict-error";
import useAuraInProgress from "../../../hooks/useAuraInProgress";
import AuraInProgressBanner from "../../../components/aura-in-progress-banner";
import { isNullOrUndefinedOrEmpty } from "../../../utils/filter-value";
import uuid from "react-uuid";
import { getDashboardBorrowerFieldErrors } from "../../../utils/required-field-helper/dashboard-borrower-required-helper";
import { getDashboardAddressFieldErrors } from "../../../utils/required-field-helper/dashboard-address-required-helper";
import { validateDashboardEmployment } from "../../../utils/required-field-helper/dashboard-employment-required-helper";
import { getDashboardAssetsFieldErrors } from "../../../utils/required-field-helper/dashboard-assets-required-helper";
import { getDashboardDeclarationsFieldErrors } from "../../../utils/required-field-helper/dashboard-declarations-required-helper";
import {
	validateDashboardClosing,
	getDashboardClosingFieldErrors,
	parseClosingDate,
} from "../../../utils/required-field-helper/dashboard-closing-required-helper";
import {
	getCreationPrerequisiteErrors,
	PREREQUISITE_REQUIRED_MESSAGE,
} from "../../../utils/required-field-helper/dashboard-prerequisite-required-helper";
import {
	getPairErrorMap,
	intersectErrors,
	pairErrorMessage,
} from "../../../utils/required-field-helper/dashboard-pairs-required-helper";
import {
	INCOME_VERIFY_TYPE_FIELD_ID,
	fromEncompassIncomeVerifyType,
	getFieldValue,
} from "../../../utils/income-verification-type";
import styles from "./index.module.scss";
import {
	BOOL_YES_NO_OPTIONS,
	EMPLOYMENT_TYPE_OPTIONS,
	APP_METHOD_OPTIONS,
	DECLARATION_QUESTIONS,
	MAX_BORROWER_PAIRS,
	MAX_EMPLOYMENT_PER_OWNER,
	MAX_OTHER_INCOME_ROWS,
	EMPTY_ERRORS,
	PAIR_READ_RETRY_MS,
	PAIR_SECTIONS,
	UNSYNCED_SNAPSHOT,
	CLOSING_ALL_FIELD_IDS,
} from "../../../utils/aura.constants";

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// ── Empty-state factories (one call per new pair) ─────────────────────────────
// Blank defaults (no pre-selected answers) — same as a new wizard loan, so required fields
// are genuinely answered rather than silently defaulted.
const emptyBorrower = () => ({
	firstName: "", lastName: "", middleName: "",
	dob: "", maritalStatus: "",
	citizenship: "", countryOfCitizenship: "",
	ssn: "", cellPhone: "", workPhone: "", homePhone: "",
	email: "", dependents: "", dependentAges: "",
	legalOtherThanSpouse: "", domesticRelationshipType: "",
});

const emptyAddr = () => ({
	street: "", unit: "", city: "", state: "", zip: "",
	foreignAddressIndicator: false, country: "",
	residencyBasisType: "", rent: "",
	durationYears: "", durationMonths: "",
	priorStreet: "", priorUnit: "", priorCity: "", priorState: "", priorZip: "",
	priorForeignAddressIndicator: false, priorCountry: "",
	mailingIsSame: false,
	mailingStreet: "", mailingUnit: "", mailingCity: "", mailingState: "", mailingZip: "",
	mailingForeignAddressIndicator: false, mailingCountry: "",
	residenceIds: {},
});

const emptyPair = (pairId = null) => ({
	pairId,
	// Stable local identity, so a save that finishes after the user switched or removed a
	// tab still lands its new Encompass pairId on the right pair.
	localKey: uuid(),
	borrower: emptyBorrower(),
	coBorrower: emptyBorrower(),
	bAddr: emptyAddr(),
	cbAddr: emptyAddr(),
	// Pair-level "Current Total Housing Payment" (firstMortgagePrincipalAndInterestAmount).
	housingPayment: "",
	employment: defaultEmploymentRows(),
	otherIncome: [],
	assets: emptyAssets(),
	bDecl: emptyDeclarations(),
	cbDecl: emptyDeclarations(),
});

const emptyProperty = () => ({
	street: "", unit: "", city: "", state: "", zip: "",
	units: "", grossRent: "",
	homeownersInsurance: "", floodInsurance: "", propertyTaxes: "", hoaDues: "",
});

const emptyClosing = () => ({
	closingDate: "", purchaseContractDate: "",
	vestingType: "", vestingWrittenAs: "",
	creditReportFee: "", creditReportProvider: "",
	appraisalFee: "", appraisalProvider: "",
	secondAppraisalFee: "", secondAppraisalProvider: "",
	processingFee: "", processingProvider: "",
	thirdPartyFee: "", thirdPartyProvider: "",
	bpcPercent: "", bpcDollars: "",
	lpcPercent: "", lpcDollars: "",
	escrowCompanyName: "", escrowStreet: "", escrowCity: "",
	escrowState: "", escrowZip: "",
	escrowOfficerName: "", escrowOfficerPhone: "", escrowOfficerEmail: "",
	poaBorrower: "", poaCoBorrower: "",
	cashOutBusinessPurpose: "", occupiedByFamily: "",
});

// ── Field helpers (no flex/width — grid parent handles sizing) ────────────────
// Label text with the red required asterisk inline (Body2 renders `text` as-is).
const labelText = (text, required) =>
	required ? (<>{text}<span className={styles.requiredAsterisk}>*</span></>) : text;

const TextField = ({ label, value, onChange, placeholder, error = false, disabled = false, required = false }) => (
	<Stack direction="column" gap={0.5}>
		{label && <Body2 text={labelText(label, required)} fontType="semibold" />}
		<OutlinedTextInput
			value={value || ""}
			handleChange={onChange}
			placeholder={placeholder || ""}
			fullWidth
			error={error}
			disabled={disabled}
		/>
	</Stack>
);

const PhoneField = ({ label, value, onChange, error = false, disabled = false, required = false }) => (
	<Stack direction="column" gap={0.5}>
		{label && <Body2 text={labelText(label, required)} fontType="semibold" />}
		<MaskedInput type="phone" value={value || ""} onInputChange={onChange} fullWidth error={error} disabled={disabled} />
	</Stack>
);

const SelectField = ({ label, value, onChange, options, error = false, disabled = false, required = false }) => (
	<Stack direction="column" gap={0.5}>
		{label && <Body2 text={labelText(label, required)} fontType="semibold" />}
		<SecondarySelect
			label=""
			value={value || ""}
			handleChange={onChange}
			options={options}
			displayEmpty
			fullWidth
			error={error}
			disabled={disabled}
		/>
	</Stack>
);

// Yes/No select bound to a true/false/null value (the wizard's declaration shape).
const BoolSelectField = ({ label, value, onChange, error = false, required = false }) => (
	<SelectField
		label={label}
		value={value === true ? "true" : value === false ? "false" : ""}
		onChange={(e) => onChange(e.target.value === "true")}
		options={BOOL_YES_NO_OPTIONS}
		error={error}
		required={required}
	/>
);

// ── Update helpers ────────────────────────────────────────────────────────────
const upd = (setter, key) => (e) =>
	setter((p) => ({ ...p, [key]: e.target.value }));
const updv = (setter, key) => (val) =>
	setter((p) => ({ ...p, [key]: val }));
const updCheck = (setter, key) => (e) =>
	setter((p) => ({ ...p, [key]: e.target.checked }));
// Row updaters take the new value directly (text/select/checkbox callers unwrap the event).
const setRowField = (setter, idx, field, value) =>
	setter((prev) => prev.map((r, i) => (i === idx ? { ...r, [field]: value } : r)));

// Shallow-merges a freshly-fetched object onto the existing local one, keeping the
// existing value for any key the fetch came back empty for. Prevents a partial/lagging
// backend read from silently blanking out fields the user already filled and saved
// (e.g. a section save that only PATCHed its own slice of the borrower-pair record).
const mergeNonEmpty = (existing, incoming) => {
	if (!incoming) return existing;
	const merged = { ...existing };
	Object.entries(incoming).forEach(([key, value]) => {
		const isEmpty = value === "" || value === null || value === undefined;
		if (!isEmpty) merged[key] = value;
	});
	return merged;
};

// Broker display name / dropdown label — shows name + email instead of NMLS ID.
// Still display-only — broker_id (not this label) is what actually identifies them under the hood.
const brokerName = (item) => `${item?.user?.first_name || ""} ${item?.user?.last_name || ""}`.trim();
const formatBrokerLabel = (item) =>
	item?.user?.email ? `${brokerName(item) || "—"} · ${item.user.email}` : brokerName(item);

const sumAmounts = (...values) =>
	values.reduce((total, v) => total + (Number(String(v ?? "").replace(/[^0-9.-]/g, "")) || 0), 0);

// Legacy Dashboard saves stored "Yes"/"No" in the Y/N closing fields.
const toClosingYN = (v) => (v === "Yes" ? "Y" : v === "No" ? "N" : v);

// Encompass Date → MM/DD/YYYY for the Dashboard's text date inputs.
const fromEncompassDate = (value) => {
	if (!value) return value;
	const iso = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
	if (iso) return `${iso[2]}/${iso[3]}/${iso[1]}`;
	const mdy = /^(\d{1,2})\/(\d{1,2})\/(\d{4})/.exec(value);
	if (mdy) return `${mdy[1].padStart(2, "0")}/${mdy[2].padStart(2, "0")}/${mdy[3]}`;
	return value;
};

// MM/DD/YYYY → UTC-midnight Date, same as create-loan-fee convertToUTCDate. Shares the
// closing validator's parse so any date that validates is also saved.
const parseDashboardDate = parseClosingDate;
const toUTCDateValue = (value) => parseDashboardDate(value) || "";

// Rebuilds a partial `closing` object from the field() GET response. Only includes a key
// when Encompass returned a real, non-empty value, so it can be merged over existing state.
const mapClosingFromFields = (fieldsResponse) => {
	const byId = {};
	(fieldsResponse || []).forEach((f) => {
		byId[String(f.fieldId).toUpperCase()] = f.value;
	});
	const get = (id) => {
		const v = byId[String(id).toUpperCase()];
		return v === undefined || v === null || v === "" ? undefined : v;
	};

	const out = {};
	Object.entries(CLOSING_FIELD_IDS).forEach(([key, id]) => {
		const v = get(id);
		if (v !== undefined) out[key] = v;
	});
	["closingDate", "purchaseContractDate"].forEach((key) => {
		if (out[key]) out[key] = fromEncompassDate(out[key]);
	});
	["poaBorrower", "poaCoBorrower", "cashOutBusinessPurpose", "occupiedByFamily"].forEach((key) => {
		if (out[key]) out[key] = toClosingYN(out[key]);
	});

	Object.assign(out, resolveClosingVesting(get));

	const lpcPercent = get(CLOSING_READ_ONLY_FIELD_IDS.lpcPercent);
	if (lpcPercent !== undefined) out.lpcPercent = lpcPercent;
	const lpcDollars = get(CLOSING_READ_ONLY_FIELD_IDS.lpcDollars);
	if (lpcDollars !== undefined) out.lpcDollars = lpcDollars;

	return out;
};

// Context values Step 10 uses to decide which closing fields apply.
const mapClosingContextFromFields = (fieldsResponse) => {
	const find = (id) =>
		(fieldsResponse || []).find((f) => String(f.fieldId).toUpperCase() === id.toUpperCase())?.value || "";
	return {
		compensationPlan: find(CLOSING_READ_ONLY_FIELD_IDS.compensationPlan),
		loanOrganization: find(CLOSING_READ_ONLY_FIELD_IDS.loanOrganization),
		occupancy: find(CLOSING_READ_ONLY_FIELD_IDS.occupancy),
		loanPurpose: find(CLOSING_READ_ONLY_FIELD_IDS.loanPurpose),
		coBorrowerName: find(CLOSING_READ_ONLY_FIELD_IDS.coBorrowerName),
	};
};

// Adds `numBusinessDays` weekdays to a date — create-loan-fee calculateBusinessDays.
const addBusinessDays = (start, numBusinessDays) => {
	const date = new Date(start.getTime());
	let added = 0;
	while (added < numBusinessDays) {
		date.setUTCDate(date.getUTCDate() + 1);
		const day = date.getUTCDay();
		if (day !== 0 && day !== 6) added++;
	}
	return date;
};

// create-loan-fee checkClosingAndPurchaseCloseDate — returns the warning to confirm, if any.
const getClosingDateWarning = (closingDate, purchaseContractDate) => {
	const closing = parseDashboardDate(closingDate);
	const purchase = parseDashboardDate(purchaseContractDate);
	if (!closing || !purchase) return "";
	if (closing > purchase) return VERBIAGE_CONSTANTS.CLOSING_DATE_AFTER_PURCHASE_CLOSE_DATE;
	const now = new Date();
	const todayUtc = new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate()));
	const tenBusinessDays = addBusinessDays(todayUtc, 10);
	if (closing < tenBusinessDays || purchase < tenBusinessDays) {
		return VERBIAGE_CONSTANTS.CLOSING_DATE_LESS_THAN_10_BUSINESS_DAYS;
	}
	return "";
};

// Income Documentation Type fallback fetch (CX.IncomeVerifyType) uses the shared
// utils/income-verification-type.js mapping — same one Qualify Panel B writes with, now
// including the two P&L types.

// ── Main component ────────────────────────────────────────────────────────────
const DashboardPage = () => {
	const navigate = useNavigate();
	const location = useLocation();
	const dispatch = useDispatch();
	const loanGuid = useSelector(selectLoanGuid);
	const currentStage = useSelector(selectCurrentStage);

	// Assert the current stage on every mount, regardless of how the user got here
	// (the sidebar's own "Dashboard" link, browser back/forward, a direct URL, etc.) —
	// previously `currentStage` was only ever set to "dashboard" by Qualify's own
	// "Continue"/"Register and continue" buttons when LEAVING Qualify. Any other way of
	// navigating back here (e.g. clicking "Dashboard" in the left sidebar mid-Qualify)
	// left `currentStage` stuck at "qualify", which made the sidebar show Qualify as
	// still "active" instead of checked/done even while sitting on Dashboard. This mirrors
	// the same fix applied to Qualify's own pages (see qualify/aura, qualify/non-aura).
	useEffect(() => {
		if (loanGuid && currentStage !== "dashboard") dispatch(setCurrentStage("dashboard"));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [loanGuid]);
	const auraResult = useSelector(selectAuraResult);
	const dataChanged = useSelector(selectApplicationDataChanged);
	const sectionStatuses = useSelector(selectSectionStatuses);
	const incomeDocumentationType = useSelector(selectIncomeDocumentationType);
	const auraRunAt = useSelector(selectAuraRunAt);
	const liabilitiesChangedSinceAura = useSelector(selectLiabilitiesChangedSinceAura);
	const dashboardSaveRequest = useSelector(selectDashboardSaveRequest);
	const selectedProgram = useSelector(selectSelectedProgram);
	const lockDecision = useSelector(selectLockDecision);
	const selectedPath = useSelector(selectSelectedPath);
	const isDSCRMode = isDSCRstyle(incomeDocumentationType);
	// Last-known-good Loan Originator/Loan Processor, cached across mounts (see the
	// mirror-to-Redux effects below `pairs`/`activePair`) since Encompass's own
	// getLoan()/getBorrowerPair() read-back for these ids isn't fully reliable.
	const persistedLoanOriginatorId = useSelector(selectPersistedLoanOriginatorId);
	const persistedLoanProcessorId = useSelector(selectPersistedLoanProcessorId);
	const xmlApplicationData = useSelector(selectXmlApplicationData);
	const loEmail = useSelector(selectLoEmail);
	const lpEmail = useSelector(selectLpEmail);
	const userDetails = useSelector(selectUserDetails);
	const selectedOrganization = useSelector(selectSelectedOrganization);
	const organizationName = useSelector(selectOrganizationName);
	const childOrganizationName = useSelector(selectChildOrganizationName);
	const impersonatedUser = useSelector(selectImpersonatedUser);

	const [getLoan] = useGetLoanMutation();
	const [getBrokerListByTitleBranch] = useGetBrokerListByTitleBranchMutation();
	const [getOrganizationDetails] = useGetOrganizationDetailsMutation();
	const [getBorrowerPair] = useGetBorrowerPairMutation();
	const [deleteBorrowerPair] = useDeleteBorrowerPairMutation();
	const [triggerGetCurrentLoanProgress] = useLazyGetCurrentLoanProgressQuery();
	useSyncAuraProgress();
	const [updateBorrowerPair] = useUpdateBorrowerPairMutation();
	const [createBorrowerPair] = useCreateBorrowerPairMutation();
	const [updateFields] = useUpdateFieldsMutation();
	const [createLoan] = useCreateLoanMutation();
	const [updateLoan] = useUpdateLoanMutation();
	const [field] = useFieldMutation();
	const [deleteVox] = useDeleteVoxMutation();
	const [deleteRealEstate] = useDeleteRealEstateMutation();
	const [fetchAEEmails] = useFetchAEEmailsMutation();
	const [getLoanDetail] = useGetLoanDetailMutation();
	const [isSaving, setIsSaving] = useState(false);
	const creatingLoanRef = useRef(false);
	const { getErrorMessage } = useErrorMessages();
	const [commonErrorData, setCommonErrorData] = useState(null);
	const handleOpenCommonErrorModal = (errorKey) => setCommonErrorData(getErrorMessage(errorKey));
	const handleCloseCommonErrorModal = () => setCommonErrorData(null);
	// 409 from any save → popup with the backend's message ("AURA is running" / "Loan was changed").
	const showConflictPopup = (err) => {
		if (!isConflictError(err)) return false;
		setCommonErrorData(getConflictPopup(err, getErrorMessage));
		return true;
	};
	// While AURA runs (CX.AUS.RESULT "In Progress") nothing on the Dashboard is written.
	const { locked: auraLocked } = useAuraInProgress(loanGuid);

	// ── Per-section save errors ───────────────────────────────────────────────
	const [borrowerError, setBorrowerError] = useState("");
	const [addressError, setAddressError] = useState("");
	const [employmentError, setEmploymentError] = useState("");
	const [assetsError, setAssetsError] = useState("");
	const [declarationsError, setDeclarationsError] = useState("");
	const [closingError, setClosingError] = useState("");
	const [continueError, setContinueError] = useState("");
	// XML applications beyond the three-pair limit that were left out.
	const [xmlPairsDropped, setXmlPairsDropped] = useState(0);

	// ── Per-section field-level errors (drive red-border highlighting) ────────
	// Pair sections keep one Set per pair (index = pair); highlighting follows the active pair.
	const [borrowerPairFieldErrors, setBorrowerPairFieldErrors] = useState([]);
	const [addressPairFieldErrors, setAddressPairFieldErrors] = useState([]);
	const [assetsPairFieldErrors, setAssetsPairFieldErrors] = useState([]);
	const [declarationsPairFieldErrors, setDeclarationsPairFieldErrors] = useState([]);
	const [closingFieldErrors, setClosingFieldErrors] = useState(new Set());

	const isExecutiveOrManager =
		userDetails?.user_roles?.includes("executive") ||
		userDetails?.user_roles?.includes("manager") ||
		userDetails?.user_roles?.includes("AE") ||
		userDetails?.user_roles?.includes("AM") ||
		userDetails?.user_roles?.includes("PA") ||
		userDetails?.user_roles?.includes("assistant");
	const isTpoSecondary = userDetails?.access_level?.includes("TPO Secondary");
	const effectiveUser = selectedUser(userDetails, impersonatedUser);
	const effectiveChannel = selectedUserWithLoanChannel(
		userDetails,
		impersonatedUser,
		selectedOrganization
	)?.loan_channel;
	const isNdcOrCorr =
		effectiveChannel === LOAN_CHANNEL_CONSTANTS.NDC ||
		effectiveChannel === LOAN_CHANNEL_CONSTANTS.CORR;
	// Same conditions edit-loan-application uses for the tpo company / branch blocks.
	const hasChildOrg = effectiveUser?.child_org_name !== "";
	const useOrgCompany = isExecutiveOrManager || isTpoSecondary || hasChildOrg;
	const includeBranch = ((isExecutiveOrManager || isTpoSecondary) && !!childOrganizationName) || hasChildOrg;

	// ── Broker list state (LO + LP dropdowns) ────────────────────────────────
	const [organizationUsers, setOrganizationUsers] = useState([]);
	const [organizationProcessors, setOrganizationProcessors] = useState([]);
	const [loanOriginatorId, setLoanOriginatorId] = useState(persistedLoanOriginatorId || "");
	const [loanProcessorId, setLoanProcessorId] = useState(persistedLoanProcessorId || "");

	// Mirror whatever loanOriginatorId/loanProcessorId resolve to (however they got
	// resolved — manual selection, the broker-list reconciliation effect, or
	// refetchLoanDetails) back into Redux, so the next mount can restore the last-known-
	// good value immediately (see the useState initializers above) instead of starting
	// blank and waiting on a fresh, not-fully-reliable Encompass fetch. Purely additive —
	// doesn't change how loanOriginatorId/loanProcessorId themselves get set.
	useEffect(() => {
		if (loanOriginatorId) dispatch(setPersistedLoanOriginatorId(loanOriginatorId));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [loanOriginatorId]);

	useEffect(() => {
		if (loanProcessorId) dispatch(setPersistedLoanProcessorId(loanProcessorId));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [loanProcessorId]);

	// Fallback: if Income Documentation Type hasn't been resolved yet (e.g. the
	// ClearPathSession progress round trip that normally restores it hasn't completed or
	// found nothing), fetch the real Encompass field (CX.IncomeVerifyType — same field
	// create-loan-terms and Qualify Panel B already read/write) and fill it in. Purely
	// additive/best-effort — never overwrites a value that's already set, and doesn't
	// touch the existing Redux-persisted flow at all.
	useEffect(() => {
		if (incomeDocumentationType || !isRealLoanGuid(loanGuid)) return;
		field({ loan_guid: loanGuid, fields: INCOME_VERIFY_TYPE_FIELD_ID })
			.unwrap()
			.then((res) => {
				// Encompass returns fieldId upper-cased (CX.INCOMEVERIFYTYPE), so the
				// lookup must be case-insensitive or it never matches.
				const encompassValue = getFieldValue(res?.response, INCOME_VERIFY_TYPE_FIELD_ID);
				const mapped = fromEncompassIncomeVerifyType(encompassValue);
				if (mapped) dispatch(setIncomeDocumentationType(mapped));
			})
			.catch(() => {
				// ignore — best-effort fallback, Redux/ClearPathSession restore still applies
			});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [loanGuid, incomeDocumentationType]);

	// Full matched broker record for the selected LO/LP — drives the read-only
	// Name/NMLS/Phone/Cell/Email display fields, same as create-loan's loan-originator
	// and loan-processor cards.
	const [loanOriginator, setLoanOriginator] = useState({});
	const [loanProcessor, setLoanProcessor] = useState({});
	const [organizationDetails, setOrganizationDetails] = useState({});
	const [childOrganizationDetails, setChildOrganizationDetails] = useState({});
	const [organizationLicense, setOrganizationLicense] = useState("");

	// ── Loan-creation gate (Income Documentation Type + LO + LP) ──────────────
	// Errors only show after a blocked save/continue, then clear live as each is selected.
	const prereqCardRef = useRef(null);
	const [showPrereqErrors, setShowPrereqErrors] = useState(false);
	const prereqErrors = getCreationPrerequisiteErrors(
		{ incomeDocumentationType, loanOriginatorId, loanProcessorId },
		isRealLoanGuid(loanGuid)
	);
	const visiblePrereqErrors = showPrereqErrors ? prereqErrors : EMPTY_ERRORS;

	// Blocks (and scrolls to the prerequisite card) while the loan doesn't exist yet and a
	// prerequisite is missing. Returns true when the action may continue.
	const passCreationGate = () => {
		if (prereqErrors.size === 0) return true;
		setShowPrereqErrors(true);
		prereqCardRef.current?.scrollIntoView?.({ behavior: "smooth", block: "center" });
		return false;
	};

	// ── Accordion open/close ──────────────────────────────────────────────────
	const [openSection, setOpenSection] = useState("borrower");

	// ── Borrower pairs — one object per pair, max 3 ───────────────────────────
	// Always starts blank: the screen must only ever show real Encompass data
	// (fetched below via getBorrowerPair/getLoan) or genuinely empty fields —
	// never fabricated placeholder data a user could mistake for the real borrower's.
	const [pairs, setPairs] = useState([emptyPair()]);

	// Active pair tab (0, 1, or 2)
	const [activePair, setActivePair] = useState(0);

	// ── Loan-level state (shared across all pairs) ────────────────────────────
	// Step 1 "Subject Property Details" — starts blank (real Encompass data only).
	const [property, setProperty] = useState(emptyProperty());
	// Step 2 "How will Credit be Provided?" (CX.LC.Credit.Provided). Locked once Encompass
	// already says "Reissue", same as the wizard (contact-information/index.jsx:837-841).
	const [creditReissueMethod, setCreditReissueMethod] = useState("");
	const [creditReissueOnEncompass, setCreditReissueOnEncompass] = useState("");
	// CX.FOREIGNNATLBORR1-6 as last read from Encompass — restores "Foreign National".
	const [foreignNationalSlots, setForeignNationalSlots] = useState(new Set());
	const [contacts, setContacts] = useState({
		loanOriginatorEmail: loEmail || "",
		loanProcessorEmail: lpEmail || "",
	});
	const [additionalContact, setAdditionalContact] = useState({
		name: "", phone: "", email: "",
	});
	// Reset-confirmation guard for changing Income Documentation Type after
	// credit-reissue/program/AURA work already exists (mirrors the mockup's setIncomeDoc guard).
	const [pendingIncomeDoc, setPendingIncomeDoc] = useState(null);
	const [closing, setClosing] = useState(emptyClosing());
	// Encompass values Step 10 reads to decide which closing fields apply (see
	// mapClosingContextFromFields).
	const [closingContext, setClosingContext] = useState({
		compensationPlan: "", loanOrganization: "", occupancy: "", loanPurpose: "", coBorrowerName: "",
	});
	// Soft date warning (create-loan-fee checkClosingAndPurchaseCloseDate) awaiting confirm.
	const [closingDateWarning, setClosingDateWarning] = useState("");
	// REO row whose linked liabilities are being edited (liability ↔ REO link).
	const [linkReoIndex, setLinkReoIndex] = useState(null);
	// Selected liability rows of the active pair, by index; selection never dirties the form.
	const [selectedLiabilities, setSelectedLiabilities] = useState(new Set());
	// Selected rows linked to a REO, awaiting the "Remove linked liabilities?" answer.
	const [pendingLinkedRemoval, setPendingLinkedRemoval] = useState(null);
	// REO row whose linked liabilities are shown in the existing drawer (PathA-2.13).
	const [linkedDrawerReo, setLinkedDrawerReo] = useState(null);
	useEffect(() => {
		setSelectedLiabilities(new Set());
	}, [activePair]);
	// Assets section, scrolled to by the post-re-issue "Review liabilities" deep-link.
	const assetsSectionRef = useRef(null);

	// ── Pair-aware setters (compatible with existing upd/updv/updCheck) ─────
	// Each setter accepts a function updater (fn), applies it to the target
	// field of the active pair only, leaving other pairs unchanged.
	const makePairSetter = (field) => (fn) =>
		setPairs((prev) =>
			prev.map((p, i) =>
				i === activePair
					? { ...p, [field]: typeof fn === "function" ? fn(p[field]) : fn }
					: p
			)
		);

	const setCurrentBorrower    = makePairSetter("borrower");
	const setCurrentCoBorrower  = makePairSetter("coBorrower");
	const setCurrentBAddr       = makePairSetter("bAddr");
	const setCurrentCbAddr      = makePairSetter("cbAddr");
	const setCurrentHousingPayment = makePairSetter("housingPayment");
	const setCurrentEmployment  = makePairSetter("employment");
	const setCurrentOtherIncome = makePairSetter("otherIncome");
	const setCurrentBDecl       = makePairSetter("bDecl");
	const setCurrentCbDecl      = makePairSetter("cbDecl");

	// Assets sub-field setters (wizard-shaped arrays: assets / otherAssets / liabilities /
	// otherLiabilities / reoProperties)
	const makeAssetSetter = (subField) => (fn) =>
		setPairs((prev) =>
			prev.map((p, i) =>
				i === activePair
					? {
						...p,
						assets: {
							...p.assets,
							[subField]: typeof fn === "function" ? fn(p.assets[subField]) : fn,
						},
					}
					: p
			)
		);

	const setCurrentAssets           = makeAssetSetter("assets");
	const setCurrentOtherAssets      = makeAssetSetter("otherAssets");
	const setCurrentLiabilities      = makeAssetSetter("liabilities");
	const setCurrentOtherLiabilities = makeAssetSetter("otherLiabilities");
	const setCurrentReoProperties    = makeAssetSetter("reoProperties");

	// ── Derived values for the active pair ───────────────────────────────────
	const p = pairs[activePair] || emptyPair();
	const { borrower, coBorrower, bAddr, cbAddr, housingPayment, employment, otherIncome, assets, bDecl, cbDecl } = p;
	const hasCoBorrower = !!coBorrower?.firstName;

	// Loan channel — resolved exactly like create-loan-fee (exec/manager roles read it from
	// the loan's TPO.X14 organization label, e.g. "Org Name (CB)").
	const selectedOrgLoanChannel =
		selectedOrganization && userDetails?.organizations
			? userDetails.organizations.find((org) => org.name === selectedOrganization)?.loan_channel
			: null;
	const rawLoanChannel = selectedUserWithLoanChannel(userDetails, impersonatedUser)?.loan_channel;
	const resolvedLoanChannel = Array.isArray(rawLoanChannel) ? rawLoanChannel[0] : rawLoanChannel;
	const loanChannel = isExecutiveOrManager
		? closingContext.loanOrganization?.match(/\(([^)]+)\)/)?.[1]?.toUpperCase()
		: selectedOrgLoanChannel || resolvedLoanChannel;
	const isCbWhs =
		loanChannel === LOAN_CHANNEL_CONSTANTS.CB || loanChannel === LOAN_CHANNEL_CONSTANTS.WHS;
	const closingValidationContext = {
		isCbWhs,
		compensationPlan: closingContext.compensationPlan,
		occupancy: closingContext.occupancy,
		hasCoBorrower: !!closingContext.coBorrowerName,
	};
	// Step 10 auto-answers the business-purpose questions from occupancy / loan purpose.
	const cashOutLocked =
		closingContext.occupancy !== "Investor" ||
		closingContext.loanPurpose === "Purchase" ||
		closingContext.loanPurpose === "NoCash-Out Refinance";
	const occupiedByFamilyLocked =
		closingContext.occupancy === "PrimaryResidence" || closingContext.occupancy === "SecondHome";

	// ── Computed section statuses — live, across every pair (as the wizard validates) ──
	const pairHasCoBorrower = (pair) => !!pair?.coBorrower?.firstName;
	const borrowerPairErrors = (pair) =>
		getDashboardBorrowerFieldErrors({
			borrower: pair.borrower,
			coBorrower: pair.coBorrower,
			property,
			additionalContact,
			loanOriginatorId,
			loanProcessorId,
			isNdcOrCorr,
		});
	const addressPairErrors = (pair) =>
		getDashboardAddressFieldErrors({
			bAddr: pair.bAddr,
			cbAddr: pair.cbAddr,
			hasCoBorrower: pairHasCoBorrower(pair),
			creditReissueMethod,
		});
	const assetsPairErrors = (pair) => getDashboardAssetsFieldErrors({ assets: pair.assets });
	const declarationsPairErrors = (pair) =>
		getDashboardDeclarationsFieldErrors({
			bDecl: pair.bDecl,
			cbDecl: pair.cbDecl,
			hasCoBorrower: pairHasCoBorrower(pair),
		});

	const borrowerErrorMap = useMemo(
		() => getPairErrorMap(pairs, borrowerPairErrors),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[pairs, property, additionalContact, loanOriginatorId, loanProcessorId, isNdcOrCorr]
	);
	const addressErrorMap = useMemo(
		() => getPairErrorMap(pairs, addressPairErrors),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[pairs, creditReissueMethod]
	);
	const assetsErrorMap = useMemo(
		() => getPairErrorMap(pairs, assetsPairErrors),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[pairs]
	);
	const declarationsErrorMap = useMemo(
		() => getPairErrorMap(pairs, declarationsPairErrors),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[pairs]
	);
	const closingErrorSet = useMemo(
		() => getDashboardClosingFieldErrors(closing, closingValidationContext),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[closing, isCbWhs, closingContext]
	);

	const borrowerStatus = borrowerErrorMap.isValid ? "complete" : "active";
	const addressStatus = addressErrorMap.isValid ? "complete" : "active";
	// Step 4 registers `validate: () => true` — no required fields.
	const employmentStatus = validateDashboardEmployment() ? "complete" : "active";
	const assetsStatus = assetsErrorMap.isValid ? "complete" : "active";
	const declarationsStatus = declarationsErrorMap.isValid ? "complete" : "active";
	const closingStatus = closingErrorSet.size === 0 ? "complete" : "active";

	// Highlight sets for the active pair: flagged by a failed Save and still invalid now, so a
	// field stops being red as soon as the user gives it a value.
	const borrowerFieldErrors = intersectErrors(borrowerPairFieldErrors[activePair], borrowerErrorMap.byPair[activePair]);
	const addressFieldErrors = intersectErrors(addressPairFieldErrors[activePair], addressErrorMap.byPair[activePair]);
	const assetsFieldErrors = intersectErrors(assetsPairFieldErrors[activePair], assetsErrorMap.byPair[activePair]);
	const declarationsFieldErrors = intersectErrors(declarationsPairFieldErrors[activePair], declarationsErrorMap.byPair[activePair]);
	const shownClosingFieldErrors = intersectErrors(closingFieldErrors, closingErrorSet);

	// Once every flagged field in a section is fixed, drop its "fields highlighted above"
	// banner too. The snapshot is only non-empty after a validation failure (a valid Save
	// clears it before calling the API), so API error banners are left alone.
	useEffect(() => {
		if (!borrowerErrorMap.isValid || !borrowerPairFieldErrors.length) return;
		setBorrowerPairFieldErrors([]);
		setBorrowerError("");
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [borrowerErrorMap.isValid]);
	useEffect(() => {
		if (!addressErrorMap.isValid || !addressPairFieldErrors.length) return;
		setAddressPairFieldErrors([]);
		setAddressError("");
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [addressErrorMap.isValid]);
	useEffect(() => {
		if (!assetsErrorMap.isValid || !assetsPairFieldErrors.length) return;
		setAssetsPairFieldErrors([]);
		setAssetsError("");
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [assetsErrorMap.isValid]);
	useEffect(() => {
		if (!declarationsErrorMap.isValid || !declarationsPairFieldErrors.length) return;
		setDeclarationsPairFieldErrors([]);
		setDeclarationsError("");
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [declarationsErrorMap.isValid]);
	useEffect(() => {
		if (closingErrorSet.size > 0 || closingFieldErrors.size === 0) return;
		setClosingFieldErrors(new Set());
		setClosingError("");
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [closingErrorSet.size]);

	// ── Pair management ───────────────────────────────────────────────────────
	const handleAddPair = () => {
		if (pairs.length >= MAX_BORROWER_PAIRS) return;
		setPairs((prev) => [...prev, emptyPair()]);
		setActivePair(pairs.length); // new pair is at index = current length
	};

	const handleRemovePair = async (pairIdx) => {
		if (pairs.length <= 1 || auraLocked) return;
		const target = pairs[pairIdx];

		if (target.pairId) {
			try {
				await deleteBorrowerPair({ loanGuid, borrowerPairId: target.pairId }).unwrap();
			} catch (err) {
				if (showConflictPopup(err)) return;
				// If the pair was already gone on the server, still remove from UI
				const detail = err?.data?.detail || "";
				if (!detail.toLowerCase().includes("not found")) return;
			}
		}

		setPairs((prev) => prev.filter((_, i) => i !== pairIdx));
		setActivePair((prev) => (prev >= pairIdx && prev > 0 ? prev - 1 : prev));
		// Per-pair highlights are indexed by pair, so they no longer line up.
		[setBorrowerPairFieldErrors, setAddressPairFieldErrors, setAssetsPairFieldErrors, setDeclarationsPairFieldErrors].forEach(
			(setErrors) => setErrors([])
		);
	};

	// ── Fetch LO / LP broker lists (same logic as create-xml-loan-application) ─
	// Broker-search parameters — shared by the list fetch below and the LO/LP name
	// safeguard's re-fetch before a save.
	const getBrokerSearchParams = () => {
		const effectiveOrgName = selectedOrganization || organizationName;
		if (
			effectiveUser?.access_level === TPO_SECONDARY ||
			effectiveUser?.access_level === BROKER_ADMIN
		) {
			// NOTE: pass the raw org name here — getBrokerListByTitleBranchApiSlice's
			// query() already calls encodeURIComponent internally. Encoding it here
			// too double-encodes the name (e.g. a space becomes %2520 instead of %20),
			// which silently returns zero brokers from the backend.
			return { brokerSearchOrgName: effectiveOrgName, includeChildren: true };
		}
		if (effectiveUser?.child_org_name !== "" && !selectedOrganization) {
			return { brokerSearchOrgName: childOrganizationName, includeChildren: false };
		}
		return { brokerSearchOrgName: effectiveOrgName, includeChildren: false };
	};

	useEffect(() => {
		const effectiveOrgName = selectedOrganization || organizationName;
		if (!effectiveOrgName) return;
		const { brokerSearchOrgName, includeChildren } = getBrokerSearchParams();

		const fetchLO = async () => {
			try {
				const results = await getBrokerListByTitleBranch({
					orgName: brokerSearchOrgName,
					title: "Loan Officer",
					include_child: includeChildren,
				}).unwrap();
				const opts = results.map((item) => ({
					...item,
					label: formatBrokerLabel(item),
					value: formatBrokerLabel(item),
				}));
				setOrganizationUsers(opts);
			} catch (_) {}
		};

		const fetchLP = async () => {
			try {
				const results = await getBrokerListByTitleBranch({
					orgName: brokerSearchOrgName,
					title: "Loan Processor",
					include_child: includeChildren,
				}).unwrap();
				const opts = results.map((item) => ({
					...item,
					label: formatBrokerLabel(item),
					value: formatBrokerLabel(item),
				}));
				setOrganizationProcessors(opts);
			} catch (_) {}
		};

		const fetchOrgDetails = async () => {
			try {
				const result = await getOrganizationDetails({
					organizationName: encodeURIComponent(effectiveOrgName),
				}).unwrap();
				setOrganizationDetails(result || {});
			} catch (_) {}
		};

		// Branch details for the tpo payload — fetched under the same condition as
		// edit-loan-application:343-351.
		const fetchChildOrgDetails = async () => {
			try {
				const result = await getOrganizationDetails({
					organizationName: encodeURIComponent(childOrganizationName),
				}).unwrap();
				setChildOrganizationDetails(result || {});
			} catch (_) {}
		};

		fetchLO();
		fetchLP();
		fetchOrgDetails();
		if (
			!selectedOrganization &&
			!!childOrganizationName &&
			(isExecutiveOrManager || isTpoSecondary || effectiveUser?.child_org_name !== "")
		) {
			fetchChildOrgDetails();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selectedOrganization, organizationName]);

	// ── Reconcile LO/LP ids against the freshly-fetched broker lists ──────────
	// The loan's saved lOID/lPID (from getLoan) may be stale, missing, or load
	// before/after the broker list — resolve by id first, falling back to email,
	// same as edit-loan-application, so the dropdown actually shows the selection.
	useEffect(() => {
		if (!organizationUsers.length) return;
		const matched = resolveBrokerFromList(
			organizationUsers,
			loanOriginatorId,
			contacts.loanOriginatorEmail
		);
		if (matched) {
			if (String(matched.broker_id) !== String(loanOriginatorId)) {
				setLoanOriginatorId(matched.broker_id);
				// An Encompass-side correction, not a user edit: the snapshot effect moves the
				// borrower snapshot forward on the render where this id has landed.
				pendingBrokerIdRef.current.lo = matched.broker_id;
			}
			setLoanOriginator(matched);
		}
	}, [organizationUsers, contacts.loanOriginatorEmail]);

	useEffect(() => {
		if (!organizationProcessors.length) return;
		const matched = resolveBrokerFromList(
			organizationProcessors,
			loanProcessorId,
			contacts.loanProcessorEmail
		);
		if (matched) {
			if (String(matched.broker_id) !== String(loanProcessorId)) {
				setLoanProcessorId(matched.broker_id);
				// An Encompass-side correction, not a user edit: the snapshot effect moves the
				// borrower snapshot forward on the render where this id has landed.
				pendingBrokerIdRef.current.lp = matched.broker_id;
			}
			setLoanProcessor(matched);
		}
	}, [organizationProcessors, contacts.loanProcessorEmail]);

	// ── Fetch existing pairs on mount ─────────────────────────────────────────
	// Merges onto whatever's already in local state (XML-seeded or from an earlier
	// fetch this session) rather than replacing wholesale — a backend response that's
	// only partially populated (e.g. only the section that's been saved so far) must
	// not blank out fields the user already filled in but haven't round-tripped yet.
	// Fetches the real borrower-pair data from Encompass and re-hydrates `pairs`.
	// Called on mount AND whenever the Borrower/Address section is opened (see the
	// fetch-on-open effect below `toggle`), so reopening a section always reflects the
	// latest Encompass-persisted values instead of a possibly-stale one-time fetch.
	// ── Hydration + change tracking (autosave safety) ─────────────────────────
	// A section only autosaves on a real loan once its first Encompass read has landed AND
	// its payload differs from the snapshot taken at that read (or at its last save), so a
	// section switch can never push blank or half-loaded state over real Encompass data.
	const hydratedRef = useRef({ pairs: false, loanDetails: false, loanFields: false, closing: false });
	const snapshotRef = useRef({});
	const resnapshotRef = useRef(new Set());
	// Broker ids set by the LO/LP list matching, waiting for the render where they land.
	const pendingBrokerIdRef = useRef({});
	// Signatures of the XML-seeded state, taken once after the upload is applied; before the
	// loan exists, autosave only runs for a section the user changed from these.
	const xmlBaselineRef = useRef(null);
	const xmlBaselinePendingRef = useRef(false);
	const SECTION_SOURCES = {
		borrower: ["pairs", "loanDetails", "loanFields"],
		address: ["pairs", "loanFields"],
		employment: ["pairs"],
		assets: ["pairs"],
		declarations: ["pairs"],
		closing: ["closing"],
	};
	const isSectionHydrated = (key) =>
		(SECTION_SOURCES[key] || []).every((source) => hydratedRef.current[source]);
	const markHydrated = (source) => {
		hydratedRef.current[source] = true;
		Object.entries(SECTION_SOURCES).forEach(([key, sources]) => {
			if (sources.includes(source)) resnapshotRef.current.add(key);
		});
	};
	const sectionSignature = (key) => {
		switch (key) {
			case "borrower":
				return JSON.stringify({
					people: pairs.map((pr) => [pr.borrower, pr.coBorrower]),
					property,
					additionalContact,
					loanOriginatorId,
					loanProcessorId,
				});
			case "address":
				return JSON.stringify({
					addr: pairs.map((pr) => [pr.bAddr, pr.cbAddr, pr.housingPayment]),
					creditReissueMethod,
				});
			case "employment":
				return JSON.stringify(pairs.map((pr) => [pr.employment, pr.otherIncome]));
			case "assets":
				return JSON.stringify(pairs.map((pr) => pr.assets));
			case "declarations":
				return JSON.stringify(pairs.map((pr) => [pr.bDecl, pr.cbDecl]));
			case "closing":
				return JSON.stringify(closing);
			default:
				return "";
		}
	};
	// After a read (or the LO/LP list matching) has been applied to state, move the section's
	// snapshot forward — but only if the section was unchanged just before that read, i.e.
	// the change came from Encompass, not the user. The first snapshot is taken once every
	// read the section depends on has landed. `prevSignatureRef` holds each section's
	// signature as of the previous render.
	const prevSignatureRef = useRef({});
	useEffect(() => {
		const pendingIds = pendingBrokerIdRef.current;
		[["lo", loanOriginatorId], ["lp", loanProcessorId]].forEach(([key, current]) => {
			if (pendingIds[key] !== undefined && String(pendingIds[key]) === String(current)) {
				resnapshotRef.current.add("borrower");
				delete pendingIds[key];
			}
		});
		Object.keys(SECTION_SOURCES).forEach((key) => {
			const signature = sectionSignature(key);
			if (resnapshotRef.current.has(key) && isSectionHydrated(key)) {
				const snapshot = snapshotRef.current[key];
				const wasClean = snapshot === undefined || prevSignatureRef.current[key] === snapshot;
				if (wasClean) snapshotRef.current[key] = signature;
			}
			prevSignatureRef.current[key] = signature;
		});
		resnapshotRef.current = new Set();
		if (xmlBaselinePendingRef.current) {
			xmlBaselinePendingRef.current = false;
			xmlBaselineRef.current = Object.fromEntries(
				Object.keys(SECTION_SOURCES).map((key) => [key, sectionSignature(key)])
			);
		}
	});
	const isSectionDirty = (key) => sectionSignature(key) !== snapshotRef.current[key];

	// A section is "complete" (Submit's "Application validation complete") only after its own
	// Save button (runSectionSave). It drops back to "review" when it stops validating, or when
	// the user changes it without that Save (autosave on leaving a section does not count).
	// "Changed" is measured from the section's data at the user's first interaction with it
	// (editBaselineRef), so data loading in from Encompass on a revisit never counts as an edit.
	const editBaselineRef = useRef({});
	const markSectionInteraction = (section) => () => {
		if (editBaselineRef.current[section] === undefined) {
			editBaselineRef.current[section] = sectionSignature(section);
		}
	};
	const liveSectionStatuses = {
		borrower: borrowerStatus,
		address: addressStatus,
		employment: employmentStatus,
		assets: assetsStatus,
		declarations: declarationsStatus,
		closing: closingStatus,
	};
	useEffect(() => {
		Object.entries(liveSectionStatuses).forEach(([section, live]) => {
			if (sectionStatuses?.[section] !== "complete") return;
			if (isRealLoanGuid(loanGuid) && !isSectionHydrated(section)) return;
			const baseline = editBaselineRef.current[section];
			const edited = baseline !== undefined && sectionSignature(section) !== baseline;
			if (live !== "complete" || edited) dispatch(syncSectionStatus({ section, status: "review" }));
		});
	});

	// Accordion state: Missing (required information missing, red), Review (valid but not
	// saved with its own Save or "Save and continue"), Complete (saved and unchanged since).
	const displaySectionStatus = (section) =>
		liveSectionStatuses[section] !== "complete"
			? "missing"
			: sectionStatuses?.[section] === "complete"
				? "complete"
				: "warning";

	// Publishes unsaved Assets & liabilities changes for the left-nav save prompt (PathA-2.11).
	const assetsUnsaved = isRealLoanGuid(loanGuid) && isSectionHydrated("assets") && isSectionDirty("assets");
	useEffect(() => {
		dispatch(setDashboardDirty(assetsUnsaved));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [assetsUnsaved]);
	useEffect(
		() => () => {
			dispatch(setDashboardDirty(false));
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[]
	);

	// Post-re-issue deep-link (PathA-2.05): open Assets & liabilities, scroll to it, and clear
	// the router state so a refresh doesn't repeat it.
	useEffect(() => {
		if (location.state?.openSection !== "assets") return;
		setOpenSection("assets");
		setTimeout(() => assetsSectionRef.current?.scrollIntoView?.({ behavior: "smooth", block: "start" }), 0);
		navigate(location.pathname, { replace: true, state: null });
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [location.state]);

	const refetchBorrowerPair = async () => {
		if (!loanGuid) return;
		try {
			const data = await getBorrowerPair({ loanGuid, includeSource: true }).unwrap();
			const list = data?.response || data;
			if (!Array.isArray(list) || !list.length) {
				if (Array.isArray(list) && isRealLoanGuid(loanGuid)) markHydrated("pairs");
				return;
			}
			// After a failed creation step the local edits win over what Encompass returns.
			const localWins = PAIR_SECTIONS.some((key) => snapshotRef.current[key] === UNSYNCED_SNAPSHOT);
			const merge = (existingValue, incoming) =>
				localWins ? mergeNonEmpty(incoming || {}, existingValue) : mergeNonEmpty(existingValue, incoming);
			const pickRows = (existingRows, incomingRows) =>
				localWins && existingRows?.length ? existingRows : incomingRows;
			setPairs((prevPairs) => {
				// Match saved pairs by pairId; a local pair with no pairId yet (XML-seeded before
				// the loan existed) is matched by position. Unsaved local pairs that match
				// nothing are kept after the server list instead of being dropped.
				const used = new Set();
				const merged = list.map((pair, idx) => {
					const pairId = pair.borrowerPairId || pair.id;
					let matchIdx = prevPairs.findIndex((pr) => pr.pairId && String(pr.pairId) === String(pairId));
					// A local pair seeded from the XML matches the server pair created from the
					// same XML application; other unsaved pairs fall back to position.
					if (matchIdx === -1) {
						matchIdx = prevPairs.findIndex((pr, i) => !used.has(i) && !pr.pairId && pr.xmlIndex === idx);
					}
					if (
						matchIdx === -1 &&
						prevPairs[idx] &&
						!prevPairs[idx].pairId &&
						(prevPairs[idx].xmlIndex === undefined || prevPairs[idx].xmlIndex === null)
					) {
						matchIdx = idx;
					}
					if (matchIdx !== -1) used.add(matchIdx);
					const existing = matchIdx !== -1 ? prevPairs[matchIdx] : emptyPair(pairId);
					// CX.FOREIGNNATLBORRn slots: 2*idx = borrower, 2*idx+1 = co-borrower.
					const borrowerMapped = pair.borrower
						? mapPersonFromReal(pair.borrower, foreignNationalSlots.has(idx * 2))
						: null;
					const coBorrowerMapped = pair.coborrower
						? mapPersonFromReal(pair.coborrower, foreignNationalSlots.has(idx * 2 + 1))
						: null;
					const residences = mapResidencesFromReal(pair.residences);
					const withMailingFlag = (addr, mapped) =>
						mapped?.mailingIsSame === undefined ? addr : { ...addr, mailingIsSame: mapped.mailingIsSame };
					const { employment: employmentRows, otherIncome: otherIncomeRows } = mapEmploymentFromReal(
						pair.employment,
						pair.otherIncomeSources
					);
					const hasAssets =
						pair.assets?.length ||
						pair.otherAssets?.length ||
						pair.liabilities?.length ||
						pair.otherLiabilities?.length ||
						pair.reoProperties?.length;
					return {
						...existing,
						pairId,
						borrower: merge(existing.borrower, borrowerMapped?.borrower),
						coBorrower: merge(existing.coBorrower, coBorrowerMapped?.borrower),
						bDecl: borrowerMapped ? merge(existing.bDecl, borrowerMapped.decl) : existing.bDecl,
						cbDecl: coBorrowerMapped ? merge(existing.cbDecl, coBorrowerMapped.decl) : existing.cbDecl,
						bAddr: residences.hasAny
							? withMailingFlag(merge(existing.bAddr, residences.bAddr), localWins ? null : borrowerMapped)
							: existing.bAddr,
						cbAddr: residences.hasAny
							? withMailingFlag(merge(existing.cbAddr, residences.cbAddr), localWins ? null : coBorrowerMapped)
							: existing.cbAddr,
						housingPayment:
							!(localWins && existing.housingPayment) &&
							pair.firstMortgagePrincipalAndInterestAmount !== undefined &&
							pair.firstMortgagePrincipalAndInterestAmount !== null
								? String(pair.firstMortgagePrincipalAndInterestAmount)
								: existing.housingPayment,
						employment: pickRows(existing.employment, employmentRows.length ? employmentRows : existing.employment),
						otherIncome: pickRows(existing.otherIncome, otherIncomeRows.length ? otherIncomeRows : existing.otherIncome),
						// Row arrays round-trip whole (wizard shape incl. Encompass ids) — a fresh
						// read replaces them so deleted/renumbered rows never linger locally.
						assets: localWins ? existing.assets : hasAssets ? mapAssetsFromReal(pair) : existing.assets,
					};
				});
				const unsaved = prevPairs.filter((pr, i) => !used.has(i) && !pr.pairId);
				return [...merged, ...unsaved].slice(0, MAX_BORROWER_PAIRS);
			});
			if (isRealLoanGuid(loanGuid)) markHydrated("pairs");
		} catch {
			// ignore — mirrors the original mount-time fetch's silent-on-failure behavior
		}
	};

	useEffect(() => {
		refetchBorrowerPair();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [loanGuid, foreignNationalSlots]);

	// Keep the active tab on an existing pair whenever the list shrinks.
	useEffect(() => {
		if (activePair > pairs.length - 1) setActivePair(Math.max(0, pairs.length - 1));
	}, [pairs.length, activePair]);

	// Fetches loan details (LO/LP identity, Step 1 subject property) from Encompass and
	// re-hydrates local state. Called on mount AND on section-open (see below), same
	// reasoning as refetchBorrowerPair above.
	const refetchLoanDetails = async () => {
		if (!loanGuid || !userDetails) return;
		try {
			const loanData = await getLoan({ loanid: loanGuid }).unwrap();
			const resp = loanData?.response;
			if (!resp) return;

			const isNdcOrCorr =
				selectedUserWithLoanChannel(userDetails, impersonatedUser, selectedOrganization)
					?.loan_channel === LOAN_CHANNEL_CONSTANTS.NDC ||
				selectedUserWithLoanChannel(userDetails, impersonatedUser, selectedOrganization)
					?.loan_channel === LOAN_CHANNEL_CONSTANTS.CORR;

			const resolvedLoEmail =
				isExecutiveOrManager || isNdcOrCorr
					? resp?.interviewerEmail || ""
					: resp?.tPO?.lOEmail?.toLowerCase() || "";

			const resolvedLpEmail = resp?.tPO?.lPEmail?.toLowerCase() || "";

			if (resolvedLoEmail) {
				setContacts((prev) => ({
					...prev,
					loanOriginatorEmail: resolvedLoEmail,
					loanProcessorEmail: resolvedLpEmail || prev.loanProcessorEmail,
				}));
			}
			if (resp?.tPO?.lOID) setLoanOriginatorId(parseInt(resp.tPO.lOID));
			if (resp?.tPO?.lPID) setLoanProcessorId(parseInt(resp.tPO.lPID));

			// Step 1 Subject Property Details — same getLoan() shape edit-loan-application reads.
			const mappedProperty = mapSubjectPropertyFromLoan(resp);
			if (Object.keys(mappedProperty).length) {
				const keepLocal = snapshotRef.current.borrower === UNSYNCED_SNAPSHOT;
				setProperty((prev) => (keepLocal ? mergeNonEmpty(mappedProperty, prev) : mergeNonEmpty(prev, mappedProperty)));
			}
			if (isRealLoanGuid(loanGuid)) markHydrated("loanDetails");
		} catch {
			// ignore — mirrors the original mount-time fetch's silent-on-failure behavior
		}
	};

	useEffect(() => {
		refetchLoanDetails();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [loanGuid, userDetails]);

	// Credit reissue (Step 2), foreign-national flags (Step 1) and the additional loan contact
	// (Step 1, edit-loan-application:677-688) — all literal field IDs.
	const refetchLoanFields = async () => {
		if (!isRealLoanGuid(loanGuid)) return;
		try {
			const res = await field({
				loan_guid: loanGuid,
				fields: [
					PROPERTY_FIELD_IDS.creditReissueMethod,
					...FOREIGN_NATIONAL_FIELD_IDS,
					...Object.values(ADDITIONAL_CONTACT_FIELD_IDS),
				].join(","),
			}).unwrap();
			const list = res?.response || [];
			// Encompass upper-cases CX.* ids in responses (CX.LC.CREDIT.PROVIDED), so match
			// case-insensitively — an exact match never found the saved credit method.
			const valueOf = (id) =>
				list.find((f) => String(f?.fieldId || "").toUpperCase() === id.toUpperCase())?.value || "";
			const credit = valueOf(PROPERTY_FIELD_IDS.creditReissueMethod);
			if (credit) {
				setCreditReissueMethod(credit);
				setCreditReissueOnEncompass(credit);
			}
			const slots = foreignNationalSlotsFromFields(list);
			setForeignNationalSlots((prev) =>
				prev.size === slots.size && [...slots].every((s) => prev.has(s)) ? prev : slots
			);
			// TPO.X61/X65/X63 hold the additional contact only on NDC/CORR loans (on other
			// channels they carry the Loan Originator's own details).
			if (isNdcOrCorr) {
				setAdditionalContact((prev) =>
					mergeNonEmpty(prev, {
						name: valueOf(ADDITIONAL_CONTACT_FIELD_IDS.name),
						phone: valueOf(ADDITIONAL_CONTACT_FIELD_IDS.phone),
						email: valueOf(ADDITIONAL_CONTACT_FIELD_IDS.email),
					})
				);
			}
			markHydrated("loanFields");
		} catch {
			// ignore — best-effort read, same as the wizard's field() fetch
		}
	};

	useEffect(() => {
		refetchLoanFields();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [loanGuid]);

	// Fetches the Closing section's saved Encompass field values (and the Step 10 visibility
	// context) and re-hydrates local `closing` state. Called on mount AND on section-open.
	const refetchClosingData = async () => {
		if (!isRealLoanGuid(loanGuid)) return;
		try {
			const res = await field({
				loan_guid: loanGuid,
				fields: CLOSING_ALL_FIELD_IDS.join(","),
			}).unwrap();
			const mapped = mapClosingFromFields(res?.response);
			const context = mapClosingContextFromFields(res?.response);
			setClosingContext(context);
			setClosing((prev) => {
				const next = mergeNonEmpty(prev, mapped);
				// Same auto-answers create-loan-fee applies on load.
				const autoCashOut =
					context.occupancy !== "Investor" ||
					context.loanPurpose === "Purchase" ||
					context.loanPurpose === "NoCash-Out Refinance";
				if (autoCashOut) next.cashOutBusinessPurpose = "N";
				if (context.occupancy === "PrimaryResidence" || context.occupancy === "SecondHome") {
					next.occupiedByFamily = "Y";
				}
				if (!next.bpcDollars && mapped.bpcPercent !== undefined) next.bpcDollars = "0.00";
				return next;
			});
			markHydrated("closing");
		} catch {
			// ignore — mirrors the original mount-time fetch's silent-on-failure behavior
		}
	};

	useEffect(() => {
		refetchClosingData();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [loanGuid]);

	// ── Fetch saved AURA qualify progress on mount ────────────────────────────
	// Runs whether this loan was entered via initLoan (brand-new upload — no saved
	// progress yet, 404 is expected and a no-op) or resumeLoan (Upload History —
	// restores selected path/program, lock decision, AURA result, conditions, etc.
	// that useSyncAuraProgress persisted on a prior visit).
	// markProgressHydrated fires in both the success and 404/failure branches —
	// useSyncAuraProgress across all 5 current-loan pages gates on it so a fresh
	// resumeLoan's blank reset values can never race this GET and overwrite real
	// saved progress on the backend before it has a chance to be restored.
	useEffect(() => {
		if (!isRealLoanGuid(loanGuid)) return;
		triggerGetCurrentLoanProgress(loanGuid)
			.unwrap()
			.then((data) => dispatch(hydrateProgress(data)))
			.catch(() => {})
			.finally(() => dispatch(markProgressHydrated()));
	}, [loanGuid]);

	useEffect(() => {
		if (!loanGuid) navigate("/current-loan");
	}, [loanGuid, navigate]);

	// ── Sync LO/LP emails from Redux whenever they change ─────────────────────
	useEffect(() => {
		setContacts((prev) => ({
			...prev,
			loanOriginatorEmail: loEmail || prev.loanOriginatorEmail,
			loanProcessorEmail: lpEmail || prev.loanProcessorEmail,
		}));
	}, [loEmail, lpEmail]);

	// ── Auto-fill from XML uploaded on the upload page ────────────────────────
	// Just populates the form so the user sees their uploaded data immediately.
	// The real Encompass loan is no longer created here — it's created lazily, on
	// the user's first section save (see ensureRealLoanCreated below), so nothing
	// is written to Encompass before the user actually does something. xmlApplicationData
	// stays in Redux (not cleared here) since ensureRealLoanCreated still needs it.
	const appliedXmlRef = useRef(false);
	useEffect(() => {
		if (!xmlApplicationData || appliedXmlRef.current) return;
		appliedXmlRef.current = true;
		applyXmlData(xmlApplicationData);
	}, [xmlApplicationData]);

	// ── Lazy real-loan creation — called from the top of every persistXData below ──
	// Creates the real Encompass loan on the user's first actual save action instead
	// of eagerly on Dashboard mount. No-ops (returns the existing guid) once a real
	// loan already exists, or if there's no XML to create from (e.g. manual entry).
	// Returns the resolved guid directly — callers must use this return value instead
	// of the outer `loanGuid` for the rest of that same save, since Redux state won't
	// reflect the new guid until after this component re-renders.
	// Error thrown when a save was stopped and the user was already told why (a modal or the
	// prerequisite card), so the section's own error banner stays empty.
	const handledError = (detail = "") => {
		const error = new Error(detail || "Save stopped");
		error.handled = !detail;
		error.data = { detail };
		return error;
	};

	// edit-loan-application's name safeguard: the LO/LP names become Encompass TPO.X61 /
	// TPO.X74, so never send a blank or "undefined undefined" name — re-resolve the broker
	// from the list (then a fresh list) first, and stop the save if it still can't be verified.
	const resolveEffectiveLoLp = async () => {
		const { brokerSearchOrgName, includeChildren } = getBrokerSearchParams();
		const resolve = async (current, list, id, email, title) => {
			let broker = current;
			if (!isInvalidLoLpName(buildLoLpFullName(broker))) return broker;
			broker = resolveBrokerFromList(list, id, email) || broker;
			if (!isInvalidLoLpName(buildLoLpFullName(broker))) return broker;
			try {
				const refreshed = await getBrokerListByTitleBranch({
					orgName: brokerSearchOrgName,
					title,
					include_child: includeChildren,
				}).unwrap();
				broker = resolveBrokerFromList(refreshed, id, email) || broker;
			} catch (_) {
				// fall through to the verification check below
			}
			return broker;
		};
		const lo = isNdcOrCorr
			? loanOriginator
			: await resolve(loanOriginator, organizationUsers, loanOriginatorId, contacts.loanOriginatorEmail, "Loan Officer");
		const lp = await resolve(loanProcessor, organizationProcessors, loanProcessorId, contacts.loanProcessorEmail, "Loan Processor");
		if ((!isNdcOrCorr && isInvalidLoLpName(buildLoLpFullName(lo))) || isInvalidLoLpName(buildLoLpFullName(lp))) {
			throw handledError(VERBIAGE_CONSTANTS.LO_LP_UNVERIFIED_MESSAGE);
		}
		if (lo !== loanOriginator) setLoanOriginator(lo);
		if (lp !== loanProcessor) setLoanProcessor(lp);
		return { lo, lp };
	};

	const buildTpoForSave = (lo, lp) => ({
		tpo: buildTpoLoanData({
			loanOriginator: lo,
			loanProcessor: lp,
			organizationDetails,
			childOrganizationDetails,
			additionalContact,
			isNdcOrCorr,
			useOrgCompany,
			includeBranch,
		}),
		...buildOriginatorInfoData(lo),
	});

	const ensureFieldsSaved = (result) => {
		if (result?.response !== "Field values updated successfully.") {
			throw handledError(result?.response || VERBIAGE_CONSTANTS.SAVE_FAILED_MESSAGE);
		}
	};

	// Contact / AE writes that failed right after createLoan — retried on the next save.
	const pendingContactWriteRef = useRef(null);

	// Step 1 contact data written right after createLoan: tpo + originator info, org fields,
	// TPO.X62 / TPO.X75 and CX.LC.AE.EMAIL*.
	const writeCreationContactData = async (guid, lo, lp, aeResult) => {
		await updateLoan({ loanGuid: guid, loanData: buildTpoForSave(lo, lp) }).unwrap();
		ensureFieldsSaved(
			await updateFields({
				loan_guid: guid,
				field_values: [
					...buildLoanContactFieldValues({ loanOriginator: lo, loanOriginatorId, loanProcessorId }),
					...buildAeEmailFieldValues(aeResult, hasChildOrg),
				],
			}).unwrap()
		);
	};

	const flushPendingContactWrite = async (guid) => {
		const pending = pendingContactWriteRef.current;
		if (!pending) return;
		try {
			const { lo, lp } = await resolveEffectiveLoLp();
			await writeCreationContactData(guid, lo, lp, pending.aeResult);
			pendingContactWriteRef.current = null;
		} catch (err) {
			throw handledError(err?.data?.detail || VERBIAGE_CONSTANTS.CONTACT_WRITE_FAILED_MESSAGE);
		}
	};

	// Everything on the Dashboard for every pair, plus the loan-level Step 1 / Step 2 values,
	// pushed to a just-created loan so Encompass matches what the user sees before any
	// refetch runs against the new guid (createLoan itself only had the uploaded XML).
	const syncLocalStateToNewLoan = async (guid) => {
		await saveAllPairs(guid, (pair) => ({
			...buildPeopleLoanData(pair),
			firstMortgagePrincipalAndInterestAmount: pair.housingPayment || "",
			residences: buildResidencesPayload(pair.bAddr, pair.cbAddr, pairHasCoBorrower(pair)),
			...buildEmploymentPayload(pair.employment, pair.otherIncome, pairHasCoBorrower(pair)),
			...buildAssetsPayload(pair.assets),
		}));
		let existingFees;
		try {
			existingFees = (await getLoan({ loanid: guid }).unwrap())?.response?.fees;
		} catch {
			existingFees = undefined;
		}
		await updateLoan({ loanGuid: guid, loanData: buildSubjectPropertyLoanData(property, existingFees) }).unwrap();
		const citizenships = pairs.flatMap((pr) => [
			pr.borrower?.citizenship,
			pairHasCoBorrower(pr) ? pr.coBorrower?.citizenship : "",
		]);
		ensureFieldsSaved(
			await updateFields({
				loan_guid: guid,
				field_values: [
					{ id: PROPERTY_FIELD_IDS.commercialUnits, value: property.units || "" },
					...buildForeignNationalFieldValues(citizenships),
					...(creditReissueMethod
						? [{ id: PROPERTY_FIELD_IDS.creditReissueMethod, value: creditReissueMethod }]
						: []),
				],
			}).unwrap()
		);
	};

	const ensureRealLoanCreated = async () => {
		if (isRealLoanGuid(loanGuid)) {
			await flushPendingContactWrite(loanGuid);
			return loanGuid;
		}
		// After a reload the uploaded file is gone (it isn't persisted), so there is nothing
		// to create the loan from — never save against the placeholder guid.
		if (!xmlApplicationData) throw handledError(VERBIAGE_CONSTANTS.NO_UPLOAD_MESSAGE);
		if (creatingLoanRef.current) throw handledError();
		// The gate is checked by every caller first; this is the last line of defence.
		if (prereqErrors.size > 0) throw handledError();
		creatingLoanRef.current = true;
		try {
			const { lo, lp } = await resolveEffectiveLoLp();

			// The wizard's step A (create-loan-application:895-920): the AE lookup must
			// return an email before a loan is created.
			let aeResult;
			try {
				aeResult = await fetchAEEmails({
					broker_id:
						userDetails?.organizations?.find((org) => org.name === selectedOrganization)?.tpo_broker_id ||
						userDetails?.user_tpo_id ||
						impersonatedUser?.user_tpo_id,
					loan_processor_broker_id: loanOriginatorId,
				}).unwrap();
			} catch (_) {
				aeResult = null;
			}
			if (!hasAeEmail(aeResult)) {
				handleOpenCommonErrorModal("AE_EMAIL_NOT_FOUND");
				throw handledError();
			}

			const normalizedXml = normalizeXmlForCreateLoan(xmlApplicationData);
			const result = await createLoan({
				...normalizedXml,
				applications: (normalizedXml?.applications || []).slice(0, MAX_BORROWER_PAIRS),
				tpo_user_id: effectiveUser?.user_tpo_id,
				selectedOrganization,
				userDetails: effectiveUser,
			}).unwrap();
			const guid = result.GUID;

			// The Dashboard only switches to the new guid once Encompass holds the Dashboard's
			// state; even if a step below fails, it switches, so a second loan is never created.
			try {
				await linkCreatedPairs(guid);
				await syncLocalStateToNewLoan(guid);
				try {
					await writeCreationContactData(guid, lo, lp, aeResult);
				} catch (_) {
					pendingContactWriteRef.current = { aeResult };
				}
				// Encompass now matches local state, so every pair section starts clean.
				PAIR_SECTIONS.forEach((key) => {
					snapshotRef.current[key] = sectionSignature(key);
				});
			} catch (err) {
				// Keep the user's edits: reads won't overwrite them and each section stays
				// "changed", so it is saved again the next time it's saved or left.
				PAIR_SECTIONS.forEach((key) => {
					snapshotRef.current[key] = UNSYNCED_SNAPSHOT;
				});
				throw err;
			} finally {
				dispatch(setLoanGuid({ loanGuid: guid, loanNumber: result.Loan_Number }));
				dispatch(clearXmlApplicationData());
			}
			return guid;
		} finally {
			creatingLoanRef.current = false;
		}
	};

	// ── Status helpers ────────────────────────────────────────────────────────
	// Switching sections autosaves whatever's in the section being left — mirrors the
	// create-loan wizard's proven "persist on navigate away, even if invalid" behavior.
	// The autosave is awaited before the next section opens, so that section's fetch-on-open
	// can't race the save and read back stale data.
	const toggle = async (key) => {
		if (openSection && openSection !== key) await autosaveSection(openSection);
		setOpenSection(openSection === key ? null : key);
	};

	// Fetch fresh from Encompass whenever a section is opened, instead of only relying on
	// the one-time mount fetch above — mirrors create-loan/edit-loan's per-step fetch pattern.
	// One redundant fetch on the very first open is harmless/idempotent.
	useEffect(() => {
		if (["borrower", "address", "employment", "assets", "declarations"].includes(openSection)) {
			refetchBorrowerPair();
		}
		if (openSection === "borrower") {
			refetchLoanDetails();
		}
		if (openSection === "address") {
			refetchLoanFields();
		}
		if (openSection === "closing") {
			refetchClosingData();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [openSection]);

	// ── Income Documentation Type — single authoritative gate for Qualify ────────
	// Changing it after credit-reissue/program/AURA work already exists resets that
	// work and re-evaluates required fields for the new product (mirrors the mockup's
	// setIncomeDoc reset guard).
	const applyIncomeDocChange = (val) => {
		dispatch(setIncomeDocumentationType(val));
	};

	const handleIncomeDocChange = (val) => {
		if (val === incomeDocumentationType) return;
		const hadWork = !!(auraRunAt || selectedProgram || lockDecision);
		if (incomeDocumentationType && hadWork) {
			setPendingIncomeDoc(val);
			return;
		}
		applyIncomeDocChange(val);
	};

	const handleConfirmIncomeDocChange = () => {
		dispatch(resetQualifyProgress());
		applyIncomeDocChange(pendingIncomeDoc);
		setPendingIncomeDoc(null);
	};

	// ── XML auto-fill helpers ─────────────────────────────────────────────────
	// The uploaded XML is normalized to the same Encompass key names the wizard uses, so
	// each section reuses its read mapper where it can. Only fields the section still has
	// are filled in.
	const formatDobFromXml = (raw) => {
		if (!raw) return "";
		// ISO: 1990-06-15T00:00:00Z  →  06/15/1990
		const iso = /^(\d{4})-(\d{2})-(\d{2})/.exec(raw);
		if (iso) return `${iso[2]}/${iso[3]}/${iso[1]}`;
		// MM-DD-YYYY already
		const mdy = /^(\d{2})-(\d{2})-(\d{4})/.exec(raw);
		if (mdy) return `${mdy[1]}/${mdy[2]}/${mdy[3]}`;
		return raw;
	};

	const mapBorrowerFromXml = (b) => {
		const legal = toBool(b?.legalOtherThanSpouse ?? b?.legalOtherThanSpouseIndicator);
		return {
			firstName: b?.firstName || "",
			lastName: b?.lastName || "",
			middleName: b?.middleName || "",
			dob: formatDobFromXml(b?.birthDate),
			maritalStatus: b?.maritalStatusType || "",
			citizenship: b?.urla2020CitizenshipResidencyType || "",
			countryOfCitizenship: b?.urla2020CountryOfCitizenship || "",
			ssn: b?.taxIdentificationIdentifier || "",
			cellPhone: b?.mobilePhone || "",
			workPhone: b?.workPhoneNumber || "",
			homePhone: b?.homePhoneNumber || "",
			email: b?.emailAddressText || "",
			dependents: b?.dependentCount != null ? String(b.dependentCount) : "",
			dependentAges: b?.dependentsAgesDescription || "",
			legalOtherThanSpouse: legal === null ? "" : String(legal),
			domesticRelationshipType: b?.domesticRelationshipType || "",
		};
	};

	const mapAddrFromXml = (b) => {
		const curr = b?.residences?.find((r) => r.residencyType === "Current") || {};
		const prior = b?.residences?.find(
			(r) => r.residencyType === "Prior" || r.residencyType === "Previous"
		) || {};
		return {
			...emptyAddr(),
			street: curr.uRLA2020StreetAddress || "",
			unit: curr.addressUnitIdentifier || "",
			city: curr.addressCity || "",
			state: curr.addressState || "",
			zip: curr.addressPostalCode || "",
			foreignAddressIndicator: curr.foreignAddressIndicator === true,
			country: curr.country || "",
			residencyBasisType: curr.residencyBasisType || "",
			rent: curr.rent != null ? String(curr.rent) : "",
			durationYears: String(curr.durationTermYears ?? curr.durationYears ?? ""),
			durationMonths: String(curr.durationTermMonths ?? curr.durationMonths ?? ""),
			priorStreet: prior.uRLA2020StreetAddress || "",
			priorUnit: prior.addressUnitIdentifier || "",
			priorCity: prior.addressCity || "",
			priorState: prior.addressState || "",
			priorZip: prior.addressPostalCode || "",
			mailingIsSame: true,
		};
	};

	const housingPaymentFromXml = (b) => {
		const curr = b?.residences?.find((r) => r.residencyType === "Current") || {};
		const amount = curr.presentHousingExpenseMonthlyPaymentAmount;
		return amount != null && amount !== "" ? String(amount) : "";
	};

	// XML employers → Step 4 employment rows (first = current, the rest previous unless
	// still current), capped at 3 per owner like the wizard.
	const mapEmploymentFromXml = (b, owner, rows) => {
		const out = [...rows];
		(b?.employers || []).slice(0, MAX_EMPLOYMENT_PER_OWNER).forEach((emp, i) => {
			const current = i === 0 || !emp.employmentEndDate;
			out.push({
				...newEmploymentRow(out, owner, current),
				foreignAddressIndicator: emp.foreignAddressIndicator === true,
				specialEmployerRelationshipIndicator: emp.employedByFamilyMemberIndicator === true,
				selfEmployedIndicator: emp.selfEmployedIndicator === true || emp.selfEmployedIndicator === "true",
				employerName: emp.employerName || "",
				uRLA2020StreetAddress: emp.employerAddressStreetLine1 || "",
				addressCity: emp.employerAddressCity || "",
				addressState: emp.employerAddressState || "",
				addressPostalCode: emp.employerAddressPostalCode || "",
				phoneNumber: emp.phoneNumber || "",
				positionDescription: emp.positionDescription || "",
				employmentStartDate: toWizardDate(emp.employmentStartDate),
				endDate: current ? "" : toWizardDate(emp.employmentEndDate),
				basePayAmount: emp.basePayAmount != null ? String(emp.basePayAmount) : "",
				overtimeAmount: emp.overtimeAmount != null ? String(emp.overtimeAmount) : "",
				bonusAmount: emp.bonusAmount != null ? String(emp.bonusAmount) : "",
				commissionsAmount: emp.commissionsAmount != null ? String(emp.commissionsAmount) : "",
				otherAmount: emp.otherAmount != null ? String(emp.otherAmount) : "",
			});
		});
		return out;
	};

	const mapEmploymentRowsFromXml = (b, cb) => {
		let rows = mapEmploymentFromXml(b, "Borrower", []);
		rows = mapEmploymentFromXml(cb, "CoBorrower", rows);
		const defaults = defaultEmploymentRows();
		if (!rows.some((r) => r.owner === "Borrower")) rows.unshift(defaults[0]);
		if (!rows.some((r) => r.owner === "CoBorrower")) rows.push(defaults[1]);
		return rows;
	};

	// Step 1 Subject Property Details from the XML's loan-level data.
	const mapPropertyFromXml = (apiData) => ({
		...emptyProperty(),
		...mapSubjectPropertyFromLoan(apiData),
	});

	// Step 5 rows — same keys as getBorrowerPair, default owner "Borrower" when missing.
	const mapAssetsFromXml = (app) => {
		const withOwner = (rows, key = "owner") =>
			(rows || []).map((r) => ({ ...r, [key]: r?.[key] || "Borrower" }));
		return mapAssetsFromReal({
			assets: withOwner(app?.assets),
			otherAssets: withOwner(app?.otherAssets, "borrowerType"),
			liabilities: withOwner(app?.liabilities),
			otherLiabilities: withOwner(app?.otherLiabilities, "borrowerType"),
			reoProperties: withOwner(app?.reoProperties),
		});
	};

	const mapOtherIncomeFromXml = (app) =>
		(app?.otherIncomeSources || []).map((s, i) => ({
			id: `OtherIncomeSource/${i + 1}`,
			owner: s?.owner || "Borrower",
			description: s?.description || "",
			monthlyAmount: s?.monthlyAmount != null ? String(s.monthlyAmount) : "",
		}));

	const applyXmlData = (apiData) => {
		const allApps = apiData?.applications || [];
		// Only the first three applications are used — the portal's borrower-pair limit.
		const apps = allApps.slice(0, MAX_BORROWER_PAIRS);
		setXmlPairsDropped(Math.max(0, allApps.length - MAX_BORROWER_PAIRS));

		const newPairs = apps.map((app, xmlIndex) => {
			const b = app.borrower || {};
			const cb = app.coBorrower || app.coborrower || {};
			return {
				...emptyPair(),
				// Which XML application (and so which createLoan pair) this pair came from.
				xmlIndex,
				borrower: mapBorrowerFromXml(b),
				coBorrower: mapBorrowerFromXml(cb),
				bAddr: mapAddrFromXml(b),
				cbAddr: mapAddrFromXml(cb),
				housingPayment: housingPaymentFromXml(b),
				employment: mapEmploymentRowsFromXml(b, cb),
				otherIncome: mapOtherIncomeFromXml(app),
				assets: mapAssetsFromXml(app),
				bDecl: mapPersonFromReal(b)?.decl || emptyDeclarations(),
				cbDecl: cb.firstName ? mapPersonFromReal(cb)?.decl || emptyDeclarations() : emptyDeclarations(),
			};
		});

		if (newPairs.length > 0) {
			setPairs(newPairs);
			setActivePair(0);
		}

		if (Object.keys(apiData?.property || {}).length > 0) {
			setProperty(mapPropertyFromXml(apiData));
		}
		xmlBaselinePendingRef.current = true;
	};

	// "Save and continue" — blocked by the creation gate; otherwise saves the open section
	// first, like the wizard's persist-on-navigate.
	const handleContinue = async () => {
		setContinueError("");
		if (auraLocked) return;
		if (!passCreationGate()) return;
		if (isRealLoanGuid(loanGuid)) {
			// Saves without the prompt, and stays here if the save fails (PathA-2.11).
			if (openSection && !(await saveSectionBeforeLeaving(openSection))) return;
		} else {
			// Qualify needs the real loan: create it here (this also saves everything on the
			// Dashboard) and stay on the page if that fails.
			setIsSaving(true);
			try {
				await ensureRealLoanCreated();
			} catch (err) {
				if (showConflictPopup(err)) return;
				if (!err?.handled) setContinueError(err?.data?.detail || VERBIAGE_CONSTANTS.SAVE_FAILED_MESSAGE);
				return;
			} finally {
				setIsSaving(false);
			}
		}
		if (!(await completeReviewedSections())) return;
		const target = getQualifyEntryPath({ selectedPath, selectedProgram });
		dispatch(setCurrentStage(target === "/current-loan/submit" ? "submit" : "qualify"));
		navigate(target);
	};

	// "Save and continue" counts as the Save of every valid section: a section in Review is
	// saved if it has unsaved changes, then marked Complete. Missing sections stay Missing.
	// Returns false (and stays here) if a save fails.
	const completeReviewedSections = async () => {
		for (const key of Object.keys(liveSectionStatuses)) {
			if (liveSectionStatuses[key] !== "complete" || sectionStatuses?.[key] === "complete") continue;
			const changed = isRealLoanGuid(loanGuid) && isSectionHydrated(key) && isSectionDirty(key);
			if (changed && !(await saveSectionBeforeLeaving(key))) return false;
			delete editBaselineRef.current[key];
			// A save is a data change since AURA; only marking an already-saved section is not.
			dispatch((changed ? setSectionStatus : syncSectionStatus)({ section: key, status: "complete" }));
		}
		return true;
	};

	// Saves one section before leaving for Qualify — like autosave, but a failure is shown
	// (inline, or the modal a handled error already opened) and stops the navigation.
	const saveSectionBeforeLeaving = async (key) => {
		const entry = SECTION_PERSISTERS[key];
		if (!entry || !isSectionHydrated(key) || !isSectionDirty(key)) return true;
		setIsSaving(true);
		try {
			await entry[0]();
			snapshotRef.current[key] = sectionSignature(key);
			return true;
		} catch (err) {
			if (showConflictPopup(err)) return false;
			if (!err?.handled) setContinueError(err?.data?.detail || VERBIAGE_CONSTANTS.SAVE_FAILED_MESSAGE);
			return false;
		} finally {
			setIsSaving(false);
		}
	};

	// Left-nav save prompt → "Save changes": validate and save Assets & liabilities, then go
	// where the client was going. Validation or save failures keep them here.
	useEffect(() => {
		if (!dashboardSaveRequest) return;
		const { target } = dashboardSaveRequest;
		dispatch(setDashboardSaveRequest(null));
		(async () => {
			if (!validatePairsForSave(assetsErrorMap, setAssetsPairFieldErrors, setAssetsError)) {
				setOpenSection("assets");
				return;
			}
			if (!(await saveSectionBeforeLeaving("assets"))) {
				setOpenSection("assets");
				return;
			}
			dispatch(setCurrentStage(target === "/current-loan/submit" ? "submit" : "qualify"));
			navigate(target);
		})();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [dashboardSaveRequest]);

	// "Save and close" — saves the open section, then leaves.
	const handleSaveAndClose = async () => {
		if (openSection) await autosaveSection(openSection);
		navigate("/home");
	};

	const showPriorAddr = (addr) =>
		addr.durationYears !== "" && addr.durationYears !== undefined && Number(addr.durationYears) < 2;

	// ── Real Encompass persistence helpers ────────────────────────────────────
	// updateBorrowerPair does a PARTIAL merge across top-level keys (borrower/coborrower/
	// residences/employment/assets/... — omitted keys are left untouched) but a FULL
	// REPLACE within any key you include. Only pass the top-level keys this section owns;
	// for `borrower`/`coborrower` specifically (shared by the Borrower, Address and
	// Declarations sections, since declarations/military/demographics and the mailing flag
	// are flatly merged into the same object), always build the COMPLETE merged object.

	// Encompass pair ids of a loan created from the XML on this visit, keyed by the local
	// pair's localKey — createLoan already made those pairs, so they must be updated, never
	// created a second time.
	const createdPairIdsRef = useRef({});

	// One pair: update it, or create it when it has no pairId yet or Encompass says it "was
	// not found" (edit-loan-application:1357-1394). The new id is stored by localKey, so it
	// lands on the right pair even if the user switched or removed a tab meanwhile.
	const savePair = async (pair, loanData, guid) => {
		const create = async () => {
			const created = await createBorrowerPair({ loanGuid: guid, data: loanData }).unwrap();
			const newId =
				created?.borrowerPairId || created?.id || created?.response?.borrowerPairId || created?.response?.id;
			if (newId) {
				// Also recorded here so later saves in the same click (closing over the old
				// `pairs`) update this pair instead of creating it again.
				createdPairIdsRef.current[pair.localKey] = newId;
				setPairs((prev) => prev.map((pr) => (pr.localKey === pair.localKey ? { ...pr, pairId: newId } : pr)));
			}
			return newId;
		};
		const pairId = pair.pairId || createdPairIdsRef.current[pair.localKey];
		if (!pairId) return create();
		try {
			await updateBorrowerPair({ loanGuid: guid, borrowerPairId: pairId, loanData }).unwrap();
			return pairId;
		} catch (err) {
			if (String(err?.data?.detail || "").includes("was not found")) return create();
			throw err;
		}
	};

	// Saves every pair one after another, as each wizard step does. `pairs` is the state
	// captured when the save started.
	const saveAllPairs = async (guid, buildLoanData) => {
		for (let index = 0; index < pairs.length; index++) {
			await savePair(pairs[index], buildLoanData(pairs[index], index), guid);
		}
	};

	// After createLoan, link each local pair to the Encompass pair created from its XML
	// application (`xmlIndex`). The read is retried while Encompass catches up; if the pairs
	// still can't be read, the save stops rather than creating pairs a second time. A pair
	// the user removed before the loan existed was still sent by createLoan, so it's deleted.
	const linkCreatedPairs = async (guid) => {
		const expected = Math.min((xmlApplicationData?.applications || []).length, MAX_BORROWER_PAIRS);
		let list = [];
		for (let attempt = 0; attempt < 3; attempt++) {
			try {
				const data = await getBorrowerPair({ loanGuid: guid }).unwrap();
				const response = data?.response || data;
				list = Array.isArray(response) ? response : [];
			} catch (_) {
				list = [];
			}
			if (list.length >= expected) break;
			await wait(PAIR_READ_RETRY_MS);
		}
		if (list.length < expected) throw handledError(VERBIAGE_CONSTANTS.PAIRS_NOT_LOADED_MESSAGE);

		const idAt = (index) => list[index]?.borrowerPairId || list[index]?.id;
		const ids = {};
		const linked = new Set();
		pairs.forEach((pr) => {
			if (pr.xmlIndex === undefined || pr.xmlIndex === null || !idAt(pr.xmlIndex)) return;
			ids[pr.localKey] = idAt(pr.xmlIndex);
			linked.add(pr.xmlIndex);
		});
		createdPairIdsRef.current = ids;
		setPairs((prev) => prev.map((pr) => (ids[pr.localKey] && !pr.pairId ? { ...pr, pairId: ids[pr.localKey] } : pr)));

		for (let index = 0; index < list.length; index++) {
			if (linked.has(index) || !idAt(index)) continue;
			try {
				await deleteBorrowerPair({ loanGuid: guid, borrowerPairId: idAt(index) }).unwrap();
			} catch (err) {
				throw handledError(err?.data?.detail || VERBIAGE_CONSTANTS.PAIR_REMOVE_FAILED_MESSAGE);
			}
		}
	};

	// Full borrower/coborrower objects for one pair (Steps 1, 2, 4 and 6 all send them).
	const buildPeopleLoanData = (pair) => {
		const withCo = pairHasCoBorrower(pair);
		return {
			borrower: buildPersonPayload(pair.borrower, pair.bDecl, {
				mailingIsSame: pair.bAddr.mailingIsSame,
				hasCoBorrower: withCo,
			}),
			...(withCo
				? {
						coborrower: buildPersonPayload(pair.coBorrower, pair.cbDecl, {
							mailingIsSame: pair.cbAddr.mailingIsSame,
							hasCoBorrower: true,
						}),
				  }
				: {}),
		};
	};

	// ── Persist functions — pure persistence, no validation/error-state. Reused by the
	// explicit "Save →" handlers below (after validation passes) AND by the autosave-on-
	// section-change dispatcher.

	// Step 1 — every pair, Subject Property Details + tpo contact data (updateLoan), then
	// units / org / LO / LP / foreign-national fields (updateFields) — edit-loan-application
	// handleNext. Deliberately does NOT blank NEWHUD.X223/X224/389/1620/URLA.X189/X190 like the
	// wizard's Step 1: sections here save in any order, so that would wipe the Borrower Paid
	// Compensation already saved in Closing.
	const persistBorrowerData = async () => {
		const { lo, lp } = await resolveEffectiveLoLp();
		const guid = await ensureRealLoanCreated();
		await saveAllPairs(guid, buildPeopleLoanData);

		// Merge the flood-insurance / property-tax fees into the loan's existing fee list
		// (edit-loan-application/index.jsx:1088-1129) so no other fee entry is dropped.
		let existingFees;
		try {
			const loanDetails = await getLoan({ loanid: guid }).unwrap();
			existingFees = loanDetails?.response?.fees;
		} catch {
			existingFees = undefined;
		}
		await updateLoan({
			loanGuid: guid,
			loanData: {
				...buildSubjectPropertyLoanData(property, existingFees),
				...buildTpoForSave(lo, lp),
			},
		}).unwrap();

		// Borrower slots across every pair — 2*i borrower, 2*i+1 co-borrower.
		const citizenships = pairs.flatMap((pr) => [
			pr.borrower?.citizenship,
			pairHasCoBorrower(pr) ? pr.coBorrower?.citizenship : "",
		]);
		ensureFieldsSaved(
			await updateFields({
				loan_guid: guid,
				field_values: [
					{ id: PROPERTY_FIELD_IDS.commercialUnits, value: property.units || "" },
					...buildLoanContactFieldValues({ loanOriginator: lo, loanOriginatorId, loanProcessorId }),
					...buildForeignNationalFieldValues(citizenships),
				],
			}).unwrap()
		);
		setForeignNationalSlots(
			new Set(citizenships.map((c, i) => (c === "Foreign National" ? i : -1)).filter((i) => i >= 0))
		);
	};

	// Step 2 — every pair's residences (+ housing payment), then credit reissue.
	const persistAddressData = async () => {
		const guid = await ensureRealLoanCreated();
		await saveAllPairs(guid, (pair) => ({
			...buildPeopleLoanData(pair),
			firstMortgagePrincipalAndInterestAmount: pair.housingPayment || "",
			residences: buildResidencesPayload(pair.bAddr, pair.cbAddr, pairHasCoBorrower(pair)),
		}));
		if (creditReissueMethod) {
			ensureFieldsSaved(
				await updateFields({
					loan_guid: guid,
					field_values: [{ id: PROPERTY_FIELD_IDS.creditReissueMethod, value: creditReissueMethod }],
				}).unwrap()
			);
			setCreditReissueOnEncompass(creditReissueMethod);
		}
	};

	// Step 4 — every pair's borrower objects + employment rows + other income sources.
	const persistEmploymentData = async () => {
		const guid = await ensureRealLoanCreated();
		await saveAllPairs(guid, (pair) => ({
			...buildPeopleLoanData(pair),
			...buildEmploymentPayload(pair.employment, pair.otherIncome, pairHasCoBorrower(pair)),
		}));
	};

	// Liabilities staged by Remove (D2): one deleteVox per pair with every saved row's VOL id,
	// before the PATCH. A failure stops the save (nothing is PATCHed) and keeps the rows
	// staged. Rows no longer in Encompass resolve to no id and need no delete. Staged rows
	// stay hidden locally until the next read replaces them.
	const removeStagedLiabilities = async (guid) => {
		const staged = pairs
			.map((pr, index) => ({ pr, index }))
			.filter(({ pr }) => pr.pairId && stagedLiabilityRemovals(pr.assets?.liabilities).length);
		if (!staged.length) return;
		try {
			const loanDetail = await getLoanDetail({ loanGuid: guid }).unwrap();
			for (const { pr, index } of staged) {
				const { applicationId, ids } = resolveLiabilityRemovalIds(
					loanDetail?.data,
					pr.pairId,
					index,
					pr.assets.liabilities
				);
				if (!ids.length) continue;
				const formData = new FormData();
				formData.append("ids", ids.join(","));
				const res = await deleteVox({
					loanGuid: guid,
					borrowerPairId: applicationId,
					type: ASSET_LIST_META.liabilities.deleteType,
					formData,
				}).unwrap();
				if (!String(res?.message || "").includes("success")) throw new Error("delete failed");
			}
		} catch (err) {
			if (!showConflictPopup(err)) handleOpenCommonErrorModal("LIABILITY_REMOVE_ERROR");
			throw handledError();
		}
	};

	// Liabilities differ from the section's last saved snapshot (PathA-2.09 re-run note).
	const liabilitiesChangedSinceSnapshot = () => {
		try {
			const saved = JSON.parse(snapshotRef.current.assets || "null") || [];
			return (
				JSON.stringify(pairs.map((pr) => pr.assets?.liabilities || [])) !==
				JSON.stringify(saved.map((a) => a?.liabilities || []))
			);
		} catch (_) {
			return true;
		}
	};

	// Step 5 — every pair's 5 row arrays only (the wizard doesn't send the borrower objects here).
	const persistAssetsData = async () => {
		const guid = await ensureRealLoanCreated();
		const liabilitiesChanged = liabilitiesChangedSinceSnapshot();
		await removeStagedLiabilities(guid);
		await saveAllPairs(guid, (pair) => buildAssetsPayload(pair.assets));
		if (liabilitiesChanged) dispatch(markLiabilitiesChanged());
		setSelectedLiabilities(new Set());
	};

	// Step 6 — declarations/military/demographics live on the borrower/coborrower objects.
	const persistDeclarationsData = async () => {
		const guid = await ensureRealLoanCreated();
		await saveAllPairs(guid, buildPeopleLoanData);
	};

	// Step 10 — same field-values array as create-loan-fee updateLoanFeeFields.
	const persistClosingData = async () => {
		const guid = await ensureRealLoanCreated();
		const values = [];
		Object.entries(CLOSING_FIELD_IDS).forEach(([key, id]) => {
			if (key === "closingDate" || key === "purchaseContractDate") {
				// Dates go as UTC midnight; a partially typed date is left untouched.
				const raw = closing[key];
				if (raw && !parseDashboardDate(raw)) return;
				values.push({ id, value: toUTCDateValue(raw) });
				return;
			}
			const entry = { id, value: closing[key] ?? "" };
			if (CLOSING_LOCKED_FIELD_KEYS.has(key)) entry.lock = true;
			values.push(entry);
		});
		Object.entries(CLOSING_FEE_MIRROR_FIELD_IDS).forEach(([key, id]) => {
			values.push({ id, value: closing[key] ?? "" });
		});
		values.push(CLOSING_FEE_PAID_TO_FIELD);
		Object.entries(CLOSING_FEE_DESCRIPTION_FIELDS).forEach(([key, desc]) => {
			if (closing[key] !== "" && closing[key] !== undefined && closing[key] !== null) values.push(desc);
		});
		if (INDIVIDUAL_VESTING_METHODS.has(closing.vestingType)) {
			values.push({ id: CLOSING_VESTING_FIELD_IDS.individual.vestingType, value: closing.vestingType });
			values.push({
				id: CLOSING_VESTING_FIELD_IDS.individual.vestingWrittenAs,
				value: closing.vestingWrittenAs || "",
			});
		} else if (ENTITY_VESTING_METHODS.has(closing.vestingType)) {
			values.push({ id: CLOSING_VESTING_FIELD_IDS.entity.vestingType, value: closing.vestingType });
			values.push({ id: CLOSING_VESTING_FIELD_IDS.individual.vestingType, value: "" });
			values.push({
				id: CLOSING_VESTING_FIELD_IDS.entity.vestingWrittenAs,
				value: closing.vestingWrittenAs || "",
			});
		}
		// Same success check as create-loan-fee — surface Encompass's message instead.
		ensureFieldsSaved(await updateFields({ loan_guid: guid, field_values: values }).unwrap());
	};

	// key → [persistFn, section-status-key] — used by both explicit Save and autosave.
	const SECTION_PERSISTERS = {
		borrower: [persistBorrowerData, "borrower"],
		address: [persistAddressData, "address"],
		employment: [persistEmploymentData, "employment"],
		assets: [persistAssetsData, "assets"],
		declarations: [persistDeclarationsData, "declarations"],
		closing: [persistClosingData, "closing"],
	};

	// Autosave the section being left — skips validation and swallows errors (a background
	// save, like the wizard's persist-on-navigate), but only when it's safe:
	//   - real loan: the section's Encompass read has landed AND the user changed something;
	//   - loan not created yet: only when the creation gate passes (otherwise it's skipped
	//     silently, so no loan is created without Income Documentation Type / LO / LP).
	const autosaveSection = async (key) => {
		if (!loanGuid || auraLocked) return;
		const entry = SECTION_PERSISTERS[key];
		if (!entry) return;
		if (isRealLoanGuid(loanGuid)) {
			if (!isSectionHydrated(key) || !isSectionDirty(key)) return;
		} else if (
			prereqErrors.size > 0 ||
			!xmlApplicationData ||
			!xmlBaselineRef.current ||
			sectionSignature(key) === xmlBaselineRef.current[key]
		) {
			// Nothing to create from, the gate fails, or the user didn't change this section
			// (an untouched Closing would otherwise write blank Step 10 fields).
			return;
		}
		setIsSaving(true);
		try {
			await entry[0]();
			snapshotRef.current[key] = sectionSignature(key);
		} catch (err) {
			// Otherwise silent — a background autosave, not a user-initiated save — but a 409
			// (AURA running / loan changed elsewhere) is always shown.
			showConflictPopup(err);
		} finally {
			setIsSaving(false);
		}
	};

	// Explicit save of one section (after its validation passed).
	const runSectionSave = async (key, setError) => {
		if (auraLocked) return;
		setIsSaving(true);
		try {
			await SECTION_PERSISTERS[key][0]();
			snapshotRef.current[key] = sectionSignature(key);
			// Saved: later changes are measured from the next interaction.
			delete editBaselineRef.current[key];
			dispatch(setSectionStatus({ section: key, status: "complete" }));
		} catch (err) {
			if (showConflictPopup(err)) return;
			if (!err?.handled) setError(err?.data?.detail || VERBIAGE_CONSTANTS.SAVE_FAILED_MESSAGE);
		} finally {
			setIsSaving(false);
		}
	};

	// ── Save handlers — gate → validate every pair → persist → status ─────────
	// A failed pair validation highlights the active pair's fields and names the first pair
	// with a problem.
	const validatePairsForSave = (errorMap, setPairFieldErrors, setError) => {
		if (errorMap.isValid) {
			setPairFieldErrors([]);
			return true;
		}
		setPairFieldErrors(errorMap.byPair);
		setError(pairErrorMessage(errorMap.firstInvalid, pairs.length));
		return false;
	};

	const handleSaveBorrower = async () => {
		setBorrowerError("");
		if (!passCreationGate()) return;
		if (!validatePairsForSave(borrowerErrorMap, setBorrowerPairFieldErrors, setBorrowerError)) return;
		await runSectionSave("borrower", setBorrowerError);
	};

	const handleSaveAddress = async () => {
		setAddressError("");
		if (!passCreationGate()) return;
		if (!validatePairsForSave(addressErrorMap, setAddressPairFieldErrors, setAddressError)) return;
		await runSectionSave("address", setAddressError);
	};

	const handleSaveEmployment = async () => {
		setEmploymentError("");
		if (!passCreationGate()) return;
		if (!validateDashboardEmployment()) return;
		await runSectionSave("employment", setEmploymentError);
	};

	const handleSaveAssets = async () => {
		setAssetsError("");
		if (!passCreationGate()) return;
		if (!validatePairsForSave(assetsErrorMap, setAssetsPairFieldErrors, setAssetsError)) return;
		await runSectionSave("assets", setAssetsError);
	};

	const handleSaveDeclarations = async () => {
		setDeclarationsError("");
		if (!passCreationGate()) return;
		if (!validatePairsForSave(declarationsErrorMap, setDeclarationsPairFieldErrors, setDeclarationsError)) {
			return;
		}
		await runSectionSave("declarations", setDeclarationsError);
	};

	const saveClosing = () => runSectionSave("closing", setClosingError);

	const handleSaveClosing = async () => {
		setClosingError("");
		if (!passCreationGate()) return;
		if (!validateDashboardClosing(closing, closingValidationContext, setClosingError)) {
			setClosingFieldErrors(getDashboardClosingFieldErrors(closing, closingValidationContext));
			return;
		}
		setClosingFieldErrors(new Set());
		// Step 10 asks the user to confirm unusual closing / contract dates before saving.
		const warning = getClosingDateWarning(closing.closingDate, closing.purchaseContractDate);
		if (warning) {
			setClosingDateWarning(warning);
			return;
		}
		await saveClosing();
	};

	const handleConfirmClosingDates = async () => {
		setClosingDateWarning("");
		await saveClosing();
	};

	// ── Row add / delete (Steps 4 & 5) ──────────────────────────────────────────
	// Rows can be added (primary → additional → previous, at most 3 per owner) but not
	// deleted, same as borrower-employment-information.
	const handleAddEmployment = (owner) => {
		setCurrentEmployment((rows) => {
			const mine = rows.filter((r) => r.owner === owner);
			if (mine.length >= MAX_EMPLOYMENT_PER_OWNER) return rows;
			// After the primary job, "+" adds a second current job; after that, previous jobs.
			const current = mine.length === 0 || mine.length === 1;
			const next = [...rows];
			// Same splice position as the wizard: slot 1 for an owner's first row, else the end.
			next.splice(mine.length === 0 ? 1 : rows.length, 0, newEmploymentRow(rows, owner, current));
			return next;
		});
	};

	const updateEmploymentRow = (row, field, value) =>
		setCurrentEmployment((rows) =>
			rows.map((r) => {
				if (r !== row) return r;
				const updated = { ...r, [field]: value };
				// Ownership share only applies to self-employment (employment-information:163-171).
				if (field === "selfEmployedIndicator" && value === "false") updated.ownershipInterestType = "";
				return updated;
			})
		);

	const handleAddOtherIncome = () =>
		setCurrentOtherIncome((rows) =>
			rows.length >= MAX_OTHER_INCOME_ROWS
				? rows
				: [
						...rows,
						{ id: `OtherIncomeSource/${rows.length + 1}`, description: "", monthlyAmount: 0, owner: "" },
				  ]
		);

	const ASSET_LIST_CONFIG = {
		assets: { setter: setCurrentAssets, label: "asset" },
		otherAssets: { setter: setCurrentOtherAssets, label: "other asset" },
		liabilities: { setter: setCurrentLiabilities, label: "liability" },
		otherLiabilities: { setter: setCurrentOtherLiabilities, label: "other liability" },
		reoProperties: { setter: setCurrentReoProperties, label: "real estate owned property" },
	};

	// New rows get the wizard card's defaults (vodIndex / volIndex / reoId), at most 25 each.
	// Liabilities: only the client's own rows count toward the cap — a credit import may
	// bring in more than 25.
	const handleAddAssetRow = (listKey) => {
		ASSET_LIST_CONFIG[listKey].setter((rows) => {
			const counted =
				listKey === "liabilities"
					? rows.filter((r) => !r?.removed && liabilitySource(r) !== LIABILITY_SOURCE.CREDIT)
					: rows;
			return counted.length >= MAX_ASSET_ROWS ? rows : [...rows, newAssetRow(listKey, rows, uuid)];
		});
	};

	// ── Liabilities review (PathA-2.06 / 2.13–2.15) ───────────────────────────
	const toggleLiabilitySelected = (idx) =>
		setSelectedLiabilities((prev) => {
			const next = new Set(prev);
			if (next.has(idx)) next.delete(idx);
			else next.add(idx);
			return next;
		});

	const toggleAllLiabilities = (selectAll) =>
		setSelectedLiabilities(
			selectAll
				? new Set((assets.liabilities || []).map((r, i) => (r?.removed ? null : i)).filter((i) => i !== null))
				: new Set()
		);

	// Staged remove (D2): rows leave the grid and the banner now, and are deleted on Save.
	const stageLiabilityRemoval = (indexes) => {
		setCurrentLiabilities((rows) => rows.map((r, i) => (indexes.has(i) ? { ...r, removed: true } : r)));
		setSelectedLiabilities(new Set());
	};

	const handleRemoveLiabilities = () => {
		const linked = (assets.liabilities || []).filter((r, i) => selectedLiabilities.has(i) && r?.reoId);
		if (linked.length) {
			setPendingLinkedRemoval(linked);
			return;
		}
		stageLiabilityRemoval(selectedLiabilities);
	};

	// Delete guard — rows already saved in Encompass are deleted there immediately, exactly
	// like Step 5 (deleteVox / delete_reo_property); unsaved rows are just dropped locally.
	const [pendingAssetDelete, setPendingAssetDelete] = useState(null);

	const requestAssetDelete = (listKey, idx) => {
		if (auraLocked) return;
		if (listKey === "reoProperties") {
			const reo = assets.reoProperties[idx];
			const linked = (assets.liabilities || []).some((l) => l?.reoId && l.reoId === reoLinkKey(reo));
			if (linked) {
				handleOpenCommonErrorModal("REAL_ESTATE_LINKED_LIABILITY");
				return;
			}
		}
		setPendingAssetDelete({ listKey, idx });
	};

	const handleConfirmAssetDelete = async () => {
		const { listKey, idx } = pendingAssetDelete || {};
		setPendingAssetDelete(null);
		if (!listKey) return;
		const config = ASSET_LIST_CONFIG[listKey];
		const row = assets[listKey]?.[idx];
		const isReo = listKey === "reoProperties";
		const removeLocally = () => config.setter((rows) => removeUnsavedAssetRow(listKey, rows, idx));
		// Rows are "saved" when they carry an Encompass id; an REO row is always looked up by its
		// altId in the loan detail instead, as the wizard's real-estate card does.
		const mayBeSaved =
			!!p.pairId && isRealLoanGuid(loanGuid) && (isReo ? !!(row?.altId || row?.reoId) : !!row?.id);
		if (!mayBeSaved) {
			// handleDeleteTempRow: drop it locally and renumber the rows after it.
			removeLocally();
			return;
		}
		const failKey = isReo ? "REAL_ESTATE_DELETE_FAILED" : "ASSET_DELETE_FAILED";
		setIsSaving(true);
		try {
			// The wizard resolves the Encompass ids from getLoanDetail (rows matched on altId,
			// liabilities on requestId) — the getBorrowerPair row id is not the one to delete.
			const loanDetail = await getLoanDetail({ loanGuid }).unwrap();
			const { itemId, applicationId } = resolveAssetDeleteIds(
				loanDetail?.data,
				p.pairId,
				activePair,
				listKey,
				row
			);
			if (isReo && applicationId && !itemId) {
				// Not in Encompass yet — nothing to delete there.
				removeLocally();
				return;
			}
			if (!itemId || !applicationId) {
				handleOpenCommonErrorModal(failKey);
				return;
			}
			const formData = new FormData();
			if (isReo) {
				formData.append("loan_guid", loanGuid);
				formData.append("application_id", applicationId);
				formData.append("reo_property_id", itemId);
				const res = await deleteRealEstate({ formData }).unwrap();
				if (!String(res?.response || "").includes("success")) {
					handleOpenCommonErrorModal("REAL_ESTATE_DELETE_RESPONSE_FAILED");
					return;
				}
			} else {
				formData.append("ids", itemId);
				const res = await deleteVox({
					loanGuid,
					borrowerPairId: applicationId,
					type: ASSET_LIST_META[listKey].deleteType,
					formData,
				}).unwrap();
				if (!String(res?.message || "").includes("success")) {
					handleOpenCommonErrorModal("DELETE_ACTION_FAILED");
					return;
				}
			}
			// Rows after the deleted one take the previous row's ids / indexes, as the wizard does.
			config.setter((rows) => reshiftAfterAssetDelete(listKey, rows, idx));
		} catch (err) {
			if (!showConflictPopup(err)) handleOpenCommonErrorModal(failKey);
		} finally {
			setIsSaving(false);
		}
	};

	// Liability ↔ REO link (Step 5 "Click to link liabilities"): toggles liability.reoId on the
	// liability matched by volIndex, as LinkLiabilityModal does (array index as a fallback).
	const toggleLiabilityLink = (liability, liabilityIdx, reoKey) =>
		setCurrentLiabilities((rows) =>
			rows.map((r, i) => {
				const same =
					liability?.volIndex !== undefined ? r.volIndex === liability.volIndex : i === liabilityIdx;
				return same ? { ...r, reoId: r.reoId === reoKey ? "" : reoKey } : r;
			})
		);

	// ── Pair tab label ─────────────────────────────────────────────────────────
	const PairTabLabel = ({ idx }) => (
		<Stack direction="row" alignItems="center" gap={0.5}>
			<span>{`Pair ${idx + 1}`}</span>
			{pairs.length > 1 && (
				<IconButton
					size="small"
					disabled={auraLocked}
					onClick={(e) => {
						e.stopPropagation();
						handleRemovePair(idx);
					}}
					sx={{ p: 0, ml: 0.25 }}
				>
					<CloseOutlined sx={{ fontSize: 14 }} />
				</IconButton>
			)}
		</Stack>
	);

	// ── Section render helpers (plain functions, not components, so inputs keep focus) ──

	// Step 1 — one person (borrower or co-borrower).
	const renderPerson = (person, setter, prefix) => {
		const err = (key) => borrowerFieldErrors.has(`${prefix}.${key}`);
		// Co-borrower fields only become required once a first name is entered (same rule as the helper).
		const req = prefix === "borrower" || !isNullOrUndefinedOrEmpty(person.firstName);
		const showCountry =
			person.citizenship === "NonPermanentResidentAlien" || person.citizenship === "Foreign National";
		return (
			<>
				<Box className={styles.formGrid}>
					<TextField label="First name" value={person.firstName} onChange={upd(setter, "firstName")} error={err("firstName")} required={req} />
					<TextField label="Middle name" value={person.middleName} onChange={upd(setter, "middleName")} placeholder="Optional" />
					<TextField label="Last name" value={person.lastName} onChange={upd(setter, "lastName")} error={err("lastName")} required={req} />
				</Box>

				<Box className={styles.formGrid}>
					<TextField label="Date of birth" value={person.dob} onChange={upd(setter, "dob")} placeholder="MM/DD/YYYY" error={err("dob")} required={req} />
					<SelectField
						label="Marital status"
						value={person.maritalStatus}
						onChange={(e) => {
							const value = e.target.value;
							// The legal-interest questions only apply to Unmarried (borrower-information:488-571).
							setter((prev) => ({
								...prev,
								maritalStatus: value,
								...(value === "Unmarried" ? {} : { legalOtherThanSpouse: "", domesticRelationshipType: "" }),
							}));
						}}
						options={maritalStatusOptions}
						error={err("maritalStatus")} required={req}
					/>
					<SelectField
						label="Citizenship"
						value={person.citizenship}
						onChange={(e) => {
							const value = e.target.value;
							setter((prev) => ({
								...prev,
								citizenship: value,
								...(value === "NonPermanentResidentAlien" || value === "Foreign National"
									? {}
									: { countryOfCitizenship: "" }),
							}));
						}}
						options={citizenshipTypeOptions}
						error={err("citizenship")} required={req}
					/>
					{showCountry && (
						<SelectField label="Country of citizenship" value={person.countryOfCitizenship} onChange={upd(setter, "countryOfCitizenship")} options={countryOptions} error={err("countryOfCitizenship")} required={req} />
					)}
				</Box>

				{person.maritalStatus === "Unmarried" && (
					<Box className={styles.formGrid}>
						<SelectField label="Is there a person who is not your legal spouse but who currently has real property rights similar to those of a legal spouse?" value={person.legalOtherThanSpouse} onChange={upd(setter, "legalOtherThanSpouse")} options={legalSpouseOptions} />
						<SelectField label="Domestic relationship type" value={person.domesticRelationshipType} onChange={upd(setter, "domesticRelationshipType")} options={relationshipTypeOptions} />
					</Box>
				)}

				<Box className={styles.formGrid}>
					<Stack direction="column" gap={0.5}>
						<Body2 text={labelText("SSN", req)} fontType="semibold" />
						<Box className={err("ssn") ? styles.ssnError : undefined}>
							<SSNMaskedInput
								placeholder="___-___-___"
								unmaskedSSN={person.ssn || ""}
								onUnmaskedSSNChange={updv(setter, "ssn")}
							/>
						</Box>
					</Stack>
					{/* Validation needs any one of the three (hasAnyPhone), so none carries its own asterisk. */}
					<PhoneField label="Cell phone" value={person.cellPhone} onChange={updv(setter, "cellPhone")} error={err("cellPhone")} />
					<PhoneField label="Work phone" value={person.workPhone} onChange={updv(setter, "workPhone")} error={err("cellPhone")} />
					<PhoneField label="Home phone" value={person.homePhone} onChange={updv(setter, "homePhone")} error={err("cellPhone")} />
				</Box>
				{req && <Body3 text={labelText(VERBIAGE_CONSTANTS.DASHBOARD_ONE_PHONE_REQUIRED, true)} extraClass={styles.phoneHint} />}

				<Box className={styles.formGrid}>
					<TextField label="Email" value={person.email} onChange={upd(setter, "email")} error={err("email")} required={req} />
					<TextField
						label="Number of dependents"
						value={person.dependents}
						onChange={(e) => {
							// Same 0–100 limit as borrower-information:1037.
							const value = e.target.value.replace(/[^0-9]/g, "");
							if (value === "" || Number(value) <= 100) setter((prev) => ({ ...prev, dependents: value }));
						}}
					/>
					<TextField label="Dependent ages" value={person.dependentAges} onChange={upd(setter, "dependentAges")} placeholder="e.g. 5, 10" />
				</Box>
			</>
		);
	};

	// Step 2 — one address block (current / prior / mailing) for one applicant.
	const renderAddressBlock = (addr, setter, prefix, block, disabled = false) => {
		const key = (part) => (block ? `${block}${part[0].toUpperCase()}${part.slice(1)}` : part);
		const err = (part) => addressFieldErrors.has(`${prefix}.${key(part)}`);
		const foreign = !!addr[key("foreignAddressIndicator")];
		return (
			<>
				<Box mb={1}>
					<CustomCheckbox checked={foreign} handleChange={updCheck(setter, key("foreignAddressIndicator"))} label="Foreign address" disabled={disabled} />
				</Box>
				<Box className={styles.formGrid}>
					<TextField label="Address line 1" value={addr[key("street")]} onChange={upd(setter, key("street"))} error={err("street")} required={!disabled} disabled={disabled} />
					<TextField label="Address line 2 / unit number" value={addr[key("unit")]} onChange={upd(setter, key("unit"))} placeholder="Optional" disabled={disabled} />
					<TextField label="City" value={addr[key("city")]} onChange={upd(setter, key("city"))} error={err("city")} required={!disabled} disabled={disabled} />
					{foreign ? (
						<TextField label="State / province" value={addr[key("state")]} onChange={upd(setter, key("state"))} error={err("state")} required={!disabled} disabled={disabled} />
					) : (
						<SelectField label="State" value={addr[key("state")]} onChange={upd(setter, key("state"))} options={stateOptions} error={err("state")} required={!disabled} disabled={disabled} />
					)}
					<TextField label={foreign ? "Postal code" : "ZIP code"} value={addr[key("zip")]} onChange={upd(setter, key("zip"))} error={err("zip")} required={!disabled} disabled={disabled} />
					{foreign && (
						<SelectField label="Country" value={addr[key("country")]} onChange={upd(setter, key("country"))} options={countryOptions} error={err("country")} required={!disabled} disabled={disabled} />
					)}
				</Box>
			</>
		);
	};

	// Step 2 — current (+ housing, duration), prior (if < 2 years) and mailing for one applicant.
	const renderApplicantAddress = (addr, setter, prefix, who) => {
		const err = (part) => addressFieldErrors.has(`${prefix}.${part}`);
		return (
			<>
				<span className={styles.sectionLabel}>{who} — current address</span>
				{renderAddressBlock(addr, setter, prefix, "")}
				<Box className={styles.formGrid}>
					<TextField
						label="Living here for (years)"
						value={addr.durationYears}
						onChange={(e) => {
							const value = e.target.value.replace(/[^0-9]/g, "");
							if (value === "" || Number(value) <= 100) setter((prev) => ({ ...prev, durationYears: value }));
						}}
						error={err("durationYears")}
						required
					/>
					<TextField
						label="Living here for (months)"
						value={addr.durationMonths}
						onChange={(e) => {
							const value = e.target.value.replace(/[^0-9]/g, "");
							if (value === "" || Number(value) <= 11) setter((prev) => ({ ...prev, durationMonths: value }));
						}}
						error={err("durationMonths")}
						required={String(addr.durationYears) === "0"}
					/>
					<CustomRadio
						id={`${prefix}-residency-basis`}
						label={labelText("Housing expenses", true)}
						value={addr.residencyBasisType}
						handleChange={(e) => {
							// contact-information: changing Own/Rent resets the housing payment
							// to 0 and clears the rent; clicking the chosen option unselects it.
							const value = e.target.value;
							setCurrentHousingPayment("0");
							setter((prev) => ({
								...prev,
								residencyBasisType: prev.residencyBasisType === value ? "" : value,
								rent: "",
							}));
						}}
						data={housingExpenseOptions}
						error={err("residencyBasisType")}
					/>
					{addr.residencyBasisType === "Own" && (
						<TextField label="Current total housing payment ($)" value={housingPayment} onChange={(e) => setCurrentHousingPayment(e.target.value)} />
					)}
					{addr.residencyBasisType === "Rent" && (
						<TextField label="Monthly rent ($)" value={addr.rent} onChange={upd(setter, "rent")} error={err("rent")} required />
					)}
				</Box>

				{showPriorAddr(addr) && (
					<>
						<span className={styles.sectionLabel}>{who} — prior address</span>
						{renderAddressBlock(addr, setter, prefix, "prior")}
					</>
				)}

				<span className={styles.sectionLabel}>{who} — mailing address</span>
				<Box mb={1}>
					<CustomCheckbox
						checked={!!addr.mailingIsSame}
						handleChange={(e) => setter((prev) => applyMailingSameAsCurrent(prev, e.target.checked))}
						label="Same as current information"
					/>
				</Box>
				{/* "Same as current" copies the current address here and locks it, as Step 2 does. */}
				{renderAddressBlock(
					addr.mailingIsSame ? applyMailingSameAsCurrent(addr, true) : addr,
					setter,
					prefix,
					"mailing",
					!!addr.mailingIsSame
				)}
			</>
		);
	};

	// Step 4 — one employment row.
	const renderEmploymentRow = (row, ownerIndex) => {
		const selfEmployed = isSelfEmployedRow(row);
		const primary = row.currentEmploymentIndicator === true && ownerIndex === 0;
		const previous = row.currentEmploymentIndicator === false || row.currentEmploymentIndicator === undefined;
		const set = (field) => (e) => updateEmploymentRow(row, field, e.target.value);
		const foreign = !!row.foreignAddressIndicator;
		const title = primary ? "Current employment" : previous ? "Previous employment" : "Additional employment";
		return (
			<React.Fragment key={`${row.owner}-${row.id}-${ownerIndex}`}>
				<Body2 text={title} fontType="semibold" />
				<Stack direction="row" gap={3} flexWrap="wrap" my={1}>
					<CustomCheckbox checked={foreign} handleChange={(e) => updateEmploymentRow(row, "foreignAddressIndicator", e.target.checked)} label="Foreign address" />
					<CustomCheckbox checked={!!row.specialEmployerRelationshipIndicator} handleChange={(e) => updateEmploymentRow(row, "specialEmployerRelationshipIndicator", e.target.checked)} label="I am employed by a family member, property seller, real estate agent, or other party to the transaction" />
				</Stack>
				<Box className={styles.formGrid}>
					<CustomRadio id={`${row.owner}-${ownerIndex}-self-employed`} label="Employment" value={selfEmployed ? "true" : row.selfEmployedIndicator === undefined ? "" : "false"} handleChange={(e) => updateEmploymentRow(row, "selfEmployedIndicator", e.target.value)} data={EMPLOYMENT_TYPE_OPTIONS} />
					<TextField label={selfEmployed ? "Business name" : "Employer name"} value={row.employerName} onChange={set("employerName")} />
					{selfEmployed && (
						<CustomRadio id={`${row.owner}-${ownerIndex}-ownership`} label="Ownership share" value={row.ownershipInterestType || ""} handleChange={set("ownershipInterestType")} data={ownershipShareOptions} />
					)}
				</Box>
				<Box className={styles.formGrid}>
					<TextField label="Address line 1" value={row.uRLA2020StreetAddress} onChange={set("uRLA2020StreetAddress")} />
					<TextField label="Address line 2 / unit" value={row.unitNumber} onChange={set("unitNumber")} placeholder="Optional" />
					<TextField label="City" value={row.addressCity} onChange={set("addressCity")} />
					{foreign ? (
						<TextField label="State / province" value={row.addressState} onChange={set("addressState")} />
					) : (
						<SelectField label="State" value={row.addressState} onChange={set("addressState")} options={stateOptions} />
					)}
					<TextField label={foreign ? "Postal code" : "ZIP code"} value={row.addressPostalCode} onChange={set("addressPostalCode")} />
					{foreign && <SelectField label="Country" value={row.country} onChange={set("country")} options={countryOptions} />}
				</Box>
				<Box className={styles.formGrid}>
					<PhoneField label="Phone" value={row.phoneNumber} onChange={(v) => updateEmploymentRow(row, "phoneNumber", v)} />
					<TextField label="Title or position" value={row.positionDescription} onChange={set("positionDescription")} />
					<TextField label="Start date" value={row.employmentStartDate} onChange={set("employmentStartDate")} placeholder="MM-DD-YYYY" />
					{previous && <TextField label="End date" value={row.endDate} onChange={set("endDate")} placeholder="MM-DD-YYYY" />}
					{previous && <TextField label="Previous gross monthly income ($)" value={row.basePayAmount} onChange={set("basePayAmount")} />}
				</Box>
				{!previous && selfEmployed && (
					<Box className={styles.formGrid}>
						<TextField label="Monthly income (or loss) ($)" value={row.employmentMonthlyIncomeAmount} onChange={set("employmentMonthlyIncomeAmount")} />
					</Box>
				)}
				{!previous && !selfEmployed && (
					<Box className={styles.formGrid}>
						<TextField label="Base ($)" value={row.basePayAmount} onChange={set("basePayAmount")} />
						<TextField label="Overtime ($)" value={row.overtimeAmount} onChange={set("overtimeAmount")} />
						<TextField label="Bonus ($)" value={row.bonusAmount} onChange={set("bonusAmount")} />
						<TextField label="Commission ($)" value={row.commissionsAmount} onChange={set("commissionsAmount")} />
						<TextField label="Other ($)" value={row.otherAmount} onChange={set("otherAmount")} />
						<TextField
							label="Total ($)"
							value={String(sumAmounts(row.basePayAmount, row.overtimeAmount, row.bonusAmount, row.commissionsAmount, row.otherAmount))}
							disabled
						/>
					</Box>
				)}
				<Divider sx={{ my: 2 }} />
			</React.Fragment>
		);
	};

	const renderOwnerEmployment = (owner, label) => {
		const rows = employment.filter((r) => r.owner === owner);
		return (
			<>
				<span className={styles.sectionLabel}>{label}</span>
				{rows.map((row, idx) => renderEmploymentRow(row, idx))}
				{rows.length < MAX_EMPLOYMENT_PER_OWNER && (
					<Box mb={3}>
						<SecondaryButton
							text={rows.length === 1 ? "+ Add additional employment" : "+ Add previous employment"}
							onClick={() => handleAddEmployment(owner)}
						/>
					</Box>
				)}
			</>
		);
	};

	// Step 6 — declarations, military and demographics for one person.
	const renderDeclarations = (decl, setter, prefix) => {
		const err = (key) => declarationsFieldErrors.has(`${prefix}.${key}`);
		const setBool = (key) => (value) => setter((prev) => ({ ...prev, [key]: value }));
		const setCheck = (key) => (e) => setter((prev) => ({ ...prev, [key]: e.target.checked }));
		const check = (key, label, errKey) => (
			<CustomCheckbox key={key} checked={!!decl[key]} handleChange={setCheck(key)} label={label} error={errKey ? err(errKey) : false} />
		);
		// Demographic checkboxes follow the wizard's exclusivity rules (utils/demographic-handlers).
		const demoCheck = (key, label, errKey) => (
			<CustomCheckbox key={key} checked={!!decl[key]} handleChange={() => setter((prev) => toggleDemographic(prev, key))} label={label} error={errKey ? err(errKey) : false} />
		);
		return (
			<>
				<span className={styles.sectionLabel}>Declaration questions</span>
				{DECLARATION_QUESTIONS.map(([key, label]) => (
					<React.Fragment key={key}>
						<Box className={styles.formGrid2}>
							<BoolSelectField label={label} value={decl[key]} onChange={setBool(key)} error={err(key)} required />
						</Box>
						{key === "intentToOccupyIndicator" && decl.intentToOccupyIndicator === true && (
							<Box className={styles.formGrid2}>
								<BoolSelectField label="If YES, have you had an ownership interest in another property in the last three years?" value={decl.homeownerPastThreeYearsIndicator} onChange={setBool("homeownerPastThreeYearsIndicator")} error={err("homeownerPastThreeYearsIndicator")} required />
							</Box>
						)}
						{key === "intentToOccupyIndicator" && decl.intentToOccupyIndicator === true && decl.homeownerPastThreeYearsIndicator === true && (
							<Box className={styles.formGrid2}>
								<SelectField label="(1) What type of property did you own: Primary Residence (PR), Second Home (SH), or Investment Property (IP)?" value={decl.priorPropertyUsageType} onChange={upd(setter, "priorPropertyUsageType")} options={propertyTypeOptions} error={err("priorPropertyUsageType")} required />
								<SelectField label="(2) How did you hold title to the property: by yourself (S), jointly with your spouse (SP), or jointly with another person (O)?" value={decl.priorPropertyTitleType} onChange={upd(setter, "priorPropertyTitleType")} options={propertyJointOptions} error={err("priorPropertyTitleType")} required />
							</Box>
						)}
						{key === "undisclosedBorrowedFundsIndicator" && decl.undisclosedBorrowedFundsIndicator === true && (
							<Box className={styles.formGrid2}>
								<TextField label="If YES, what is the amount of this money? ($)" value={decl.undisclosedBorrowedFundsAmount} onChange={upd(setter, "undisclosedBorrowedFundsAmount")} error={err("undisclosedBorrowedFundsAmount")} required />
							</Box>
						)}
						{key === "bankruptcyIndicator" && decl.bankruptcyIndicator === true && (
							<Stack direction="row" gap={3} flexWrap="wrap" mb={2} alignItems="center">
								<Body2 text={labelText("If YES, identify the type(s) of bankruptcy:", true)} fontType="semibold" />
								{check("bankruptcyIndicatorChapterSeven", "Chapter 7", "bankruptcyChapters")}
								{check("bankruptcyIndicatorChapterEleven", "Chapter 11", "bankruptcyChapters")}
								{check("bankruptcyIndicatorChapterTwelve", "Chapter 12", "bankruptcyChapters")}
								{check("bankruptcyIndicatorChapterThirteen", "Chapter 13", "bankruptcyChapters")}
							</Stack>
						)}
					</React.Fragment>
				))}

				<span className={styles.sectionLabel}>Military service</span>
				<Box className={styles.formGrid2}>
					<BoolSelectField label="Did you (or your deceased spouse) ever serve, or are you currently serving, in the United States Armed Forces?" value={decl.selfDeclaredMilitaryServiceIndicator} onChange={setBool("selfDeclaredMilitaryServiceIndicator")} error={err("selfDeclaredMilitaryServiceIndicator")} required />
				</Box>
				{decl.selfDeclaredMilitaryServiceIndicator === true && (
					<Stack direction="column" gap={0.5} mb={2}>
						{check("activeDuty", "Currently serving on active duty with projected expiration date of service/tour", "militaryService")}
						{decl.activeDuty && (
							<Box className={styles.formGrid}>
								<TextField label="Projected expiration date" value={decl.militaryServiceExpectedCompletionDate} onChange={upd(setter, "militaryServiceExpectedCompletionDate")} placeholder="MM/DD/YYYY" />
							</Box>
						)}
						{check("veteran", "Currently retired, discharged, or separated from service", "militaryService")}
						{check("reserveNationalGuardReserveActivated", "Only period of service was as a non-activated member of the Reserve or National Guard", "militaryService")}
						{check("spousalVABenefitsEligibilityIndicator", "Surviving spouse", "militaryService")}
					</Stack>
				)}

				<span className={styles.sectionLabel}>Demographic information provided through</span>
				<Box className={styles.formGrid}>
					<SelectField label="Application taken by" value={decl.applicationTakenMethodType} onChange={upd(setter, "applicationTakenMethodType")} options={APP_METHOD_OPTIONS} error={err("applicationTakenMethodType")} required />
					<SelectField label="Was the ethnicity of the borrower collected on the basis of visual observation or surname?" value={decl.isEthnicityBasedOnVisual} onChange={upd(setter, "isEthnicityBasedOnVisual")} options={YNOptions} error={err("isEthnicityBasedOnVisual")} required />
					<SelectField label="Was the race of the borrower collected on the basis of visual observation or surname?" value={decl.isRaceBasedOnVisual} onChange={upd(setter, "isRaceBasedOnVisual")} options={YNOptions} error={err("isRaceBasedOnVisual")} required />
					<SelectField label="Was the sex of the borrower collected on the basis of visual observation or surname?" value={decl.isSexBasedOnVisual} onChange={upd(setter, "isSexBasedOnVisual")} options={YNOptions} error={err("isSexBasedOnVisual")} required />
				</Box>

				<span className={styles.sectionLabel}>Demographic information</span>
				<Box mb={1}><Body2 text={labelText("Ethnicity", true)} fontType="semibold" /></Box>
				<Stack direction="row" gap={3} flexWrap="wrap" mb={1}>
					{demoCheck("hmdaEthnicityHispanicLatinoIndicator", "Hispanic or Latino", "ethnicity")}
					{demoCheck("hmdaEthnicityNotHispanicLatinoIndicator", "Not Hispanic or Latino", "ethnicity")}
					{demoCheck("hmdaEthnicityDoNotWishIndicator", "I do not wish to furnish this information", "ethnicity")}
				</Stack>
				{decl.hmdaEthnicityHispanicLatinoIndicator && (
					<Stack direction="row" gap={3} flexWrap="wrap" mb={1}>
						{demoCheck("hmdaMexicanIndicator", "Mexican")}
						{demoCheck("hmdaPuertoRicanIndicator", "Puerto Rican")}
						{demoCheck("hmdaCubanIndicator", "Cuban")}
						{demoCheck("hmdaHispanicLatinoOtherOriginIndicator", "Other Hispanic or Latino")}
					</Stack>
				)}
				{decl.hmdaEthnicityHispanicLatinoIndicator && decl.hmdaHispanicLatinoOtherOriginIndicator && (
					<Box className={styles.formGrid} mb={2}>
						<TextField label="Other Hispanic or Latino" value={decl.hmdaOtherHispanicLatinoOrigin} onChange={upd(setter, "hmdaOtherHispanicLatinoOrigin")} />
					</Box>
				)}

				<Box mb={1}><Body2 text={labelText("Race", true)} fontType="semibold" /></Box>
				<Stack direction="row" gap={3} flexWrap="wrap" mb={1}>
					{demoCheck("hmdaAmericanIndianIndicator", "American Indian or Alaska Native", "race")}
					{demoCheck("hmdaAsianIndicator", "Asian", "race")}
					{demoCheck("hmdaAfricanAmericanIndicator", "Black or African American", "race")}
					{demoCheck("hmdaPacificIslanderIndicator", "Native Hawaiian or Other Pacific Islander", "race")}
					{demoCheck("hmdaWhiteIndicator", "White", "race")}
					{demoCheck("hmdaRaceDoNotWishProvideIndicator", "I do not wish to furnish this information", "race")}
				</Stack>
				{decl.hmdaAmericanIndianIndicator && (
					<Box className={styles.formGrid} mb={2}>
						<TextField label="Name of enrolled or principal tribe" value={decl.hmdaAmericanIndianTribe} onChange={(e) => setter((prev) => setAmericanIndianTribe(prev, e.target.value))} />
					</Box>
				)}
				{decl.hmdaAsianIndicator && (
					<>
						<Stack direction="row" gap={3} flexWrap="wrap" mb={1}>
							{demoCheck("hmdaAsianIndianIndicator", "Asian Indian")}
							{demoCheck("hmdaChineseIndicator", "Chinese")}
							{demoCheck("hmdaFilipinoIndicator", "Filipino")}
							{demoCheck("hmdaJapaneseIndicator", "Japanese")}
							{demoCheck("hmdaKoreanIndicator", "Korean")}
							{demoCheck("hmdaVietnameseIndicator", "Vietnamese")}
							{demoCheck("hmdaAsianOtherRaceIndicator", "Other Asian")}
						</Stack>
						{decl.hmdaAsianOtherRaceIndicator && (
							<Box className={styles.formGrid} mb={2}>
								<TextField label="Other Asian" value={decl.hmdaOtherAsianRace} onChange={upd(setter, "hmdaOtherAsianRace")} />
							</Box>
						)}
					</>
				)}
				{decl.hmdaPacificIslanderIndicator && (
					<>
						<Stack direction="row" gap={3} flexWrap="wrap" mb={1}>
							{demoCheck("hmdaNativeHawaiianIndicator", "Native Hawaiian")}
							{demoCheck("hmdaGuamanianOrChamorroIndicator", "Guamanian or Chamorro")}
							{demoCheck("hmdaSamoanIndicator", "Samoan")}
							{demoCheck("hmdaPacificIslanderOtherIndicator", "Other Pacific Islander")}
						</Stack>
						{decl.hmdaPacificIslanderOtherIndicator && (
							<Box className={styles.formGrid} mb={2}>
								<TextField label="Other Pacific Islander" value={decl.hmdaOtherPacificIslanderRace} onChange={upd(setter, "hmdaOtherPacificIslanderRace")} />
							</Box>
						)}
					</>
				)}

				<Box mb={1}><Body2 text={labelText("Sex", true)} fontType="semibold" /></Box>
				<Stack direction="row" gap={3} flexWrap="wrap" mb={3}>
					{demoCheck("hmdaGendertypeFemaleIndicator", "Female", "sex")}
					{demoCheck("hmdaGendertypeMaleIndicator", "Male", "sex")}
					{demoCheck("hmdaGendertypeDoNotWishIndicator", "I do not wish to furnish this information", "sex")}
				</Stack>
			</>
		);
	};

	const assetErr = (key) => assetsFieldErrors.has(key);
	const deleteRowButton = (listKey, idx) => (
		<IconButton size="small" title="Delete" onClick={() => requestAssetDelete(listKey, idx)} sx={{ alignSelf: "flex-end" }}>
			<CloseOutlined fontSize="small" />
		</IconButton>
	);

	// ── Render ────────────────────────────────────────────────────────────────
	return (
		<CustomLayout>
			<Box className={styles.pageRoot}>
				<LoanInfoCard loanId={loanGuid} />

				<AuraInProgressBanner show={auraLocked} />

				<Stack direction="row" alignItems="center" justifyContent="space-between" mb={3}>
					<Stack direction="column">
						<Heading4 text="Step 2: Application Dashboard" fontType="semibold" />
						<Body3
							text="Review and complete each section before submitting."
							extraClass={styles.subTitle}
						/>
					</Stack>
				</Stack>

				{auraResult === "ineligible" && (
					<CustomAlert
						subject="AURA Ineligible"
						details="This loan returned AURA Ineligible. You may still complete the application and submit — manual underwriter review will apply."
						severity="warning"
						extraClass={styles.alertSpacing}
					/>
				)}
				{!auraResult && (
					<CustomAlert
						subject="AURA not run"
						details="AURA has not been run for this loan. For best results, run AURA before submission."
						severity="warning"
						extraClass={styles.alertSpacing}
					/>
				)}
				{dataChanged && auraResult && (
					<CustomAlert
						subject="Application data changed"
						details="Application data has changed since AURA was last run. Consider re-running AURA for updated eligibility."
						severity="warning"
						extraClass={styles.alertSpacing}
					/>
				)}

				{xmlPairsDropped > 0 && (
					<CustomAlert
						subject="Only three borrower pairs are supported"
						details={`The uploaded file has ${xmlPairsDropped + MAX_BORROWER_PAIRS} borrower pairs. Only the first ${MAX_BORROWER_PAIRS} are used for this loan; the other ${xmlPairsDropped} ${xmlPairsDropped === 1 ? "is" : "are"} not included.`}
						severity="warning"
						extraClass={styles.alertSpacing}
					/>
				)}

				<ProcessingModal open={isSaving} text="Saving…" />

				<CommanErrorModal
					isOpen={!!commonErrorData}
					onClose={handleCloseCommonErrorModal}
					title={commonErrorData?.title}
					message={commonErrorData?.message}
					errorKey={commonErrorData?.error_key}
					errorCode={commonErrorData?.code}
					errorResponse={commonErrorData?.error_response}
				/>

				<ConfirmationModal
					open={!!pendingAssetDelete}
					text={`Are you sure you want to delete this ${ASSET_LIST_CONFIG[pendingAssetDelete?.listKey]?.label || "row"}?`}
					confirmText="Delete"
					handleConfirm={handleConfirmAssetDelete}
					closeText="Cancel"
					handleClose={() => setPendingAssetDelete(null)}
				/>

				<ConfirmationModal
					open={!!pendingLinkedRemoval}
					text={pendingLinkedRemoval ? linkedRemovalText(pendingLinkedRemoval) : ""}
					confirmText="Remove"
					handleConfirm={() => {
						setPendingLinkedRemoval(null);
						stageLiabilityRemoval(selectedLiabilities);
					}}
					closeText="Cancel"
					handleClose={() => setPendingLinkedRemoval(null)}
				/>

				<TemporaryDrawer
					drawerOpen={linkedDrawerReo !== null}
					drawerDirection="right"
					drawerType="linked-liabilities"
					toggleDrawer={() => () => setLinkedDrawerReo(null)}
					handleCloseDrawer={() => setLinkedDrawerReo(null)}
					selectedTab={activePair}
					liabilitiesData={pairs.map((pr) => (pr.assets?.liabilities || []).filter((r) => !r?.removed))}
					selectedRealEstate={linkedDrawerReo ? reoLinkKey(linkedDrawerReo) : null}
					selectedRealEstateData={linkedDrawerReo}
				/>

				<ConfirmationModal
					open={!!closingDateWarning}
					text={closingDateWarning}
					confirmText="Yes, save"
					handleConfirm={handleConfirmClosingDates}
					closeText="Cancel"
					handleClose={() => setClosingDateWarning("")}
				/>

				<ConfirmationModal
					open={!!pendingIncomeDoc}
					text="Changing the Income Documentation Type will reset your credit re-issue, selected program, and re-evaluate the required fields for the new product. Continue?"
					confirmText="Continue"
					handleConfirm={handleConfirmIncomeDocChange}
					closeText="Cancel"
					handleClose={() => setPendingIncomeDoc(null)}
				/>

				<Box className={styles.prereqCard} ref={prereqCardRef}>
					<Stack direction="column" gap={0.5} mb={1}>
						<Body3
							text="Set the Income Documentation Type before continuing — it determines which sections apply below and gates Qualify."
							extraClass={styles.subTitle}
						/>
					</Stack>
					<Box className={styles.prereqField}>
						<SelectField
							label="Income Documentation Type"
							value={incomeDocumentationType}
							onChange={(e) => handleIncomeDocChange(e.target.value)}
							options={INCOME_VERIFICATION_TYPES}
							error={visiblePrereqErrors.has("incomeDocumentationType")}
							required
						/>
					</Box>

					<Divider sx={{ my: 2 }} />
					<Stack direction="column" gap={0.5} mb={1.5}>
						<span className={styles.sectionLabel}>
							And please confirm the Loan Originator and Loan Processor for this loan.
						</span>
						<Body3
							text="These apply to every submission. Selecting each one fills the contact details in the Loan Contacts section below."
							extraClass={styles.subTitle}
						/>
					</Stack>
					<Box className={styles.formGrid2}>
						<Stack direction="column" gap={0.5}>
							<Body2 text={labelText("Loan originator", true)} fontType="semibold" />
							<SecondarySelectLOLP
								displayEmpty
								fullWidth
								value={loanOriginatorId || ""}
								handleChange={(e) => {
									const id = e.target.value;
									setLoanOriginatorId(id);
									const match = organizationUsers.find(
										(u) => String(u.broker_id) === String(id)
									);
									setLoanOriginator(match || {});
									setContacts((prev) => ({
										...prev,
										loanOriginatorEmail: match?.user?.email || "",
									}));
								}}
								options={[...organizationUsers].sort((a, b) => a.value.localeCompare(b.value))}
								showOrgName
								error={borrowerFieldErrors.has("loanOriginatorId") || visiblePrereqErrors.has("loanOriginatorId")}
							/>
							{loanOriginatorId && (
								<Body3
									text={`${brokerName(loanOriginator)} · ${loanOriginator?.organization?.name || ""}`}
									extraClass={styles.subTitle}
								/>
							)}
						</Stack>
						<Stack direction="column" gap={0.5}>
							<Body2 text={labelText("Loan processor", true)} fontType="semibold" />
							<SecondarySelectLOLP
								displayEmpty
								fullWidth
								value={loanProcessorId || ""}
								handleChange={(e) => {
									const id = e.target.value;
									setLoanProcessorId(id);
									const match = organizationProcessors.find(
										(u) => String(u.broker_id) === String(id)
									);
									setLoanProcessor(match || {});
									setContacts((prev) => ({
										...prev,
										loanProcessorEmail: match?.user?.email || "",
									}));
								}}
								options={[...organizationProcessors].sort((a, b) => a.value.localeCompare(b.value))}
								showOrgName
								error={borrowerFieldErrors.has("loanProcessorId") || visiblePrereqErrors.has("loanProcessorId")}
							/>
							{loanProcessorId && (
								<Body3
									text={`${brokerName(loanProcessor)} · ${loanProcessor?.organization?.name || ""}`}
									extraClass={styles.subTitle}
								/>
							)}
						</Stack>
					</Box>
					{visiblePrereqErrors.size > 0 && <RequiredError text={PREREQUISITE_REQUIRED_MESSAGE} />}
				</Box>

				<Stack direction="column" gap={1.5} mb={4}>

					{/* ── Loan contacts ──────────────────────────────────────────── */}
					{/* Read-only review of the LO/LP picked in the prerequisite card above —
					    all fields here are disabled (org name/address/NMLS/license come from
					    the organization record, not editable here); the only live control is
					    each "Change originator/processor" dropdown, kept in sync with the
					    prerequisite card's selection since both read/write the same state. */}
					<QualifyAccordion
						title="Loan contacts"
						subTitle="Loan Originator and Loan Processor are selected in the prerequisite section above. Their details populate here for review; edits route through your Account Executive."
						status={loanOriginatorId && loanProcessorId ? "complete" : "missing"}
						isOpen={openSection === "loanContacts"}
						onToggle={() => toggle("loanContacts")}
					>
						<LoanOriginator
							organizationName={selectedOrganization || organizationName}
							organizationDetails={organizationDetails}
							loanOriginatorEmail={contacts.loanOriginatorEmail}
							setLoanOriginatorEmail={(val) =>
								setContacts((prev) => ({ ...prev, loanOriginatorEmail: val }))
							}
							loanOriginatorId={loanOriginatorId}
							setLoanOriginatorId={setLoanOriginatorId}
							organizationUsers={organizationUsers}
							loanOriginator={loanOriginator}
							setLoanOriginator={setLoanOriginator}
							setOrganizationLicense={setOrganizationLicense}
							propertyState={property.state}
							manualOriginator={false}
							setManualOriginator={() => {}}
							originatorData={{}}
							setOriginatorData={() => {}}
							disabled={false}
							viewLoan={false}
							error={borrowerFieldErrors.has("loanOriginatorId")}
							setHelpText={() => {}}
							setExtraHelpText={() => {}}
							handleOpenHelpText={() => {}}
							allowManualInput={false}
						/>
						<LoanProcessor
							organizationName={selectedOrganization || organizationName}
							organizationDetails={organizationDetails}
							loanProcessorEmail={contacts.loanProcessorEmail}
							setLoanProcessorEmail={(val) =>
								setContacts((prev) => ({ ...prev, loanProcessorEmail: val }))
							}
							loanProcessorId={loanProcessorId}
							setLoanProcessorId={setLoanProcessorId}
							organizationProcessor={organizationProcessors}
							loanProcessor={loanProcessor}
							propertyState={property.state}
							viewLoan={false}
							disabled={false}
							error={borrowerFieldErrors.has("loanProcessorId")}
						/>
					</QualifyAccordion>

					{/* ── 1 Borrower (Add New Loan Step 1) ──────────────────────────── */}
					<QualifyAccordion
						stepLabel="1"
						title="Borrower & co-borrower"
						status={displaySectionStatus("borrower")}
						onInteract={markSectionInteraction("borrower")}
						isOpen={openSection === "borrower"}
						onToggle={() => toggle("borrower")}
					>
						{/* Pair tabs + Add button */}
						<Stack direction="row" alignItems="center" mb={2} sx={{ borderBottom: 1, borderColor: "divider" }}>
							<Tabs
								value={activePair}
								onChange={(_, v) => setActivePair(v)}
								sx={{ flex: 1, minHeight: 36 }}
								TabIndicatorProps={{ style: { height: 2 } }}
							>
								{pairs.map((_, idx) => (
									<Tab
										key={idx}
										value={idx}
										label={<PairTabLabel idx={idx} />}
										sx={{ minHeight: 36, py: 0.5, textTransform: "none", fontSize: 13 }}
									/>
								))}
							</Tabs>
							{pairs.length < MAX_BORROWER_PAIRS && (
								<IconButton
									size="small"
									onClick={handleAddPair}
									title="Add borrower pair"
									sx={{ ml: 1 }}
								>
									<AddOutlined fontSize="small" />
								</IconButton>
							)}
						</Stack>

						<span className={styles.sectionLabel}>Borrower — Pair {activePair + 1}</span>
						{renderPerson(borrower, setCurrentBorrower, "borrower")}

						<Divider sx={{ my: 2 }} />
						<span className={styles.sectionLabel}>Co-borrower — Pair {activePair + 1}</span>
						{renderPerson(coBorrower, setCurrentCoBorrower, "coBorrower")}

						<Divider sx={{ my: 2 }} />
						<span className={styles.sectionLabel}>Subject property details (shared)</span>
						<Box className={styles.formGrid}>
							<TextField label="Address line 1" value={property.street} onChange={upd(setProperty, "street")} error={borrowerFieldErrors.has("property.street")} required />
							<TextField label="Address line 2 / unit" value={property.unit} onChange={upd(setProperty, "unit")} placeholder="Optional" />
							<TextField label="City" value={property.city} onChange={upd(setProperty, "city")} error={borrowerFieldErrors.has("property.city")} required />
							<SelectField label="State" value={property.state} onChange={upd(setProperty, "state")} options={stateOptions} error={borrowerFieldErrors.has("property.state")} required />
							<TextField label="Postal code" value={property.zip} onChange={upd(setProperty, "zip")} error={borrowerFieldErrors.has("property.zip")} required />
						</Box>
						<Box className={styles.formGrid}>
							<TextField
								label="Number of units owned"
								value={property.units}
								onChange={(e) => {
									// Same 0–999 limit as property-details:50-61.
									const value = e.target.value.replace(/[^0-9]/g, "");
									if (value === "" || Number(value) <= 999) setProperty((prev) => ({ ...prev, units: value }));
								}}
								error={borrowerFieldErrors.has("property.units")} required
							/>
							<TextField label="Expected gross rental income ($)" value={property.grossRent} onChange={upd(setProperty, "grossRent")} error={borrowerFieldErrors.has("property.grossRent")} required />
							<TextField label="Homeowner's insurance ($)" value={property.homeownersInsurance} onChange={upd(setProperty, "homeownersInsurance")} error={borrowerFieldErrors.has("property.homeownersInsurance")} required />
							<TextField label="Flood insurance ($)" value={property.floodInsurance} onChange={upd(setProperty, "floodInsurance")} />
							<TextField label="Property taxes ($)" value={property.propertyTaxes} onChange={upd(setProperty, "propertyTaxes")} error={borrowerFieldErrors.has("property.propertyTaxes")} required />
							<TextField label="HOA / project dues ($)" value={property.hoaDues} onChange={upd(setProperty, "hoaDues")} />
						</Box>

						{/* Additional loan contact — NDC / CORR only, as in edit-loan-application. */}
						{isNdcOrCorr && (
							<>
								<Divider sx={{ my: 2 }} />
								<span className={styles.sectionLabel}>Additional loan contact (shared)</span>

								<Box className={styles.formGrid}>
									<TextField label="Name" value={additionalContact.name} onChange={upd(setAdditionalContact, "name")} placeholder="Optional" />
									<PhoneField label="Phone" value={additionalContact.phone} onChange={updv(setAdditionalContact, "phone")} error={borrowerFieldErrors.has("additionalContact.phone")} required={!isNullOrUndefinedOrEmpty(additionalContact.name)} />
									<TextField label="Email" value={additionalContact.email} onChange={upd(setAdditionalContact, "email")} error={borrowerFieldErrors.has("additionalContact.email")} required={!isNullOrUndefinedOrEmpty(additionalContact.name)} />
								</Box>
							</>
						)}

						{borrowerError && <RequiredError text={borrowerError} />}
						<Box className={styles.saveRow}>
							<PrimaryButton text="Save borrower →" onClick={handleSaveBorrower} disabled={isSaving || auraLocked} />
						</Box>
					</QualifyAccordion>

					{/* ── 2 Address (Add New Loan Step 2) ──────────────────────────── */}
					<QualifyAccordion
						stepLabel="2"
						title={`Address${pairs.length > 1 ? ` — Pair ${activePair + 1}` : ""}`}
						status={displaySectionStatus("address")}
						onInteract={markSectionInteraction("address")}
						isOpen={openSection === "address"}
						onToggle={() => toggle("address")}
					>
						{renderApplicantAddress(bAddr, setCurrentBAddr, "bAddr", "Borrower")}

						{hasCoBorrower && (
							<>
								<Divider sx={{ my: 2 }} />
								{renderApplicantAddress(cbAddr, setCurrentCbAddr, "cbAddr", "Co-borrower")}
							</>
						)}

						<Divider sx={{ my: 2 }} />
						<Box className={styles.formGrid}>
							<SelectField
								label="How will credit be provided?"
								value={creditReissueMethod}
								onChange={(e) => setCreditReissueMethod(e.target.value)}
								options={creditReissueOptions}
								error={addressFieldErrors.has("creditReissueMethod")}
								required
								disabled={creditReissueOnEncompass === "Reissue"}
							/>
						</Box>

						{addressError && <RequiredError text={addressError} />}
						<Box className={styles.saveRow}>
							<PrimaryButton text="Save address →" onClick={handleSaveAddress} disabled={isSaving || auraLocked} />
						</Box>
					</QualifyAccordion>

					{/* ── 3 Employment & income (Add New Loan Step 4) ───────────────── */}
					<QualifyAccordion
						stepLabel="3"
						title={`Employment & income${pairs.length > 1 ? ` — Pair ${activePair + 1}` : ""}`}
						subTitle={isDSCRMode ? "Not applicable for DSCR" : undefined}
						status={displaySectionStatus("employment")}
						onInteract={markSectionInteraction("employment")}
						isOpen={openSection === "employment"}
						onToggle={() => toggle("employment")}
					>
						{/* Read-only (set in Qualify), sized like the other inputs. */}
						<Box className={styles.formGrid}>
							<TextField
								label="Income doc type"
								value={
									INCOME_VERIFICATION_TYPES.find((o) => o.value === incomeDocumentationType)?.label ||
									"Not set"
								}
								onChange={() => {}}
								disabled
							/>
						</Box>

						{renderOwnerEmployment("Borrower", "Borrower employment")}
						{hasCoBorrower && renderOwnerEmployment("CoBorrower", "Co-borrower employment")}

						<span className={styles.sectionLabel}>Income from other sources</span>
						{otherIncome.map((row, idx) => (
							<Box key={row.id || idx} className={styles.formGridSm}>
								<SelectField label={idx === 0 ? "Borrower / co-borrower" : ""} value={row.owner} onChange={(e) => setRowField(setCurrentOtherIncome, idx, "owner", e.target.value)} options={hasCoBorrower ? incomeOwnerOptions : incomeOwnerOptions.filter((o) => o.value === "Borrower")} />
								<SelectField label={idx === 0 ? "Income source" : ""} value={row.description} onChange={(e) => setRowField(setCurrentOtherIncome, idx, "description", e.target.value)} options={additionalIncomeSourceOptions} />
								<TextField label={idx === 0 ? "Monthly income ($)" : ""} value={row.monthlyAmount === 0 ? "" : row.monthlyAmount} onChange={(e) => setRowField(setCurrentOtherIncome, idx, "monthlyAmount", e.target.value)} />
							</Box>
						))}
						{otherIncome.length < MAX_OTHER_INCOME_ROWS && (
							<Box mb={3}>
								<SecondaryButton text="+ Add income source" onClick={handleAddOtherIncome} />
							</Box>
						)}

						{employmentError && <RequiredError text={employmentError} />}
						<Box className={styles.saveRow}>
							<PrimaryButton text="Save employment →" onClick={handleSaveEmployment} disabled={isSaving || auraLocked} />
						</Box>
					</QualifyAccordion>

					{/* ── 4 Assets & Liabilities (Add New Loan Step 5) ──────────────── */}
					<div ref={assetsSectionRef} />
					<QualifyAccordion
						stepLabel="4"
						title={
							(isDSCRMode ? "Assets & reserves" : "Assets & liabilities") +
							(pairs.length > 1 ? ` — Pair ${activePair + 1}` : "")
						}
						status={displaySectionStatus("assets")}
						onInteract={markSectionInteraction("assets")}
						isOpen={openSection === "assets"}
						onToggle={() => toggle("assets")}
					>
						<span className={styles.sectionLabel}>Assets</span>
						{assets.assets.map((row, idx) => (
							<Stack key={row.id || idx} direction="row" gap={1} alignItems="flex-end">
								<Box className={styles.formGrid5} sx={{ flex: 1 }}>
									<SelectField label={idx === 0 ? "Account owner" : ""} value={row.owner} onChange={(e) => setRowField(setCurrentAssets, idx, "owner", e.target.value)} options={ownerOptions} />
									<SelectField label={idx === 0 ? "Account type" : ""} value={row.assetType} onChange={(e) => setRowField(setCurrentAssets, idx, "assetType", e.target.value)} options={assetTypeOptions} />
									<TextField label={idx === 0 ? "Financial institution" : ""} value={row.holderName} onChange={(e) => setRowField(setCurrentAssets, idx, "holderName", e.target.value)} />
									<TextField label={idx === 0 ? "Account number" : ""} value={row.accountIdentifier} onChange={(e) => setRowField(setCurrentAssets, idx, "accountIdentifier", e.target.value)} />
									<TextField label={idx === 0 ? "Cash or market value ($)" : ""} value={row.urla2020CashOrMarketValueAmount} onChange={(e) => setRowField(setCurrentAssets, idx, "urla2020CashOrMarketValueAmount", e.target.value)} />
								</Box>
								{deleteRowButton("assets", idx)}
							</Stack>
						))}
						<Box mb={3}>
							<SecondaryButton text="+ Add asset" onClick={() => handleAddAssetRow("assets")} />
						</Box>

						<span className={styles.sectionLabel}>Other assets</span>
						{assets.otherAssets.map((row, idx) => (
							<Stack key={row.id || idx} direction="row" gap={1} alignItems="flex-end">
								<Box className={styles.formGrid} sx={{ flex: 1 }}>
									<SelectField label={idx === 0 ? "Owner" : ""} value={row.borrowerType} onChange={(e) => setRowField(setCurrentOtherAssets, idx, "borrowerType", e.target.value)} options={ownerOptions} />
									<SelectField label={idx === 0 ? "Type" : ""} value={row.assetType} onChange={(e) => setRowField(setCurrentOtherAssets, idx, "assetType", e.target.value)} options={otherAssetsTypeOptions} />
									<SelectField label={idx === 0 ? "Description" : ""} value={row.otherDescription} onChange={(e) => setRowField(setCurrentOtherAssets, idx, "otherDescription", e.target.value)} options={otherAssetsDescriptionOptions} disabled={row.assetType !== "Other"} />
									<TextField label={idx === 0 ? "Cash or market value ($)" : ""} value={row.cashOrMarketValue} onChange={(e) => setRowField(setCurrentOtherAssets, idx, "cashOrMarketValue", e.target.value)} />
								</Box>
								{deleteRowButton("otherAssets", idx)}
							</Stack>
						))}
						<Box mb={3}>
							<SecondaryButton text="+ Add other asset" onClick={() => handleAddAssetRow("otherAssets")} />
						</Box>

						<span className={styles.sectionLabel}>Liabilities</span>
						<LiabilitiesReview
							rows={assets.liabilities}
							selected={selectedLiabilities}
							onToggleAll={toggleAllLiabilities}
							onRemove={handleRemoveLiabilities}
							isDSCR={isDSCRMode}
							showAuraNote={!!auraRunAt && liabilitiesChangedSinceAura}
						/>
						{assets.liabilities.map((row, idx) => row?.removed ? null : (
							<React.Fragment key={row.id || idx}>
								<LiabilityRowTags
									row={row}
									checked={selectedLiabilities.has(idx)}
									onToggle={() => toggleLiabilitySelected(idx)}
								/>
								<Stack direction="row" gap={1} alignItems="flex-end">
									<Box className={styles.formGrid} sx={{ flex: 1 }}>
										<SelectField label={idx === 0 ? "Account owner" : ""} value={row.owner} onChange={(e) => setRowField(setCurrentLiabilities, idx, "owner", e.target.value)} options={ownerOptions} />
										<SelectField label={idx === 0 ? "Account type" : ""} value={row.liabilityType} onChange={(e) => setRowField(setCurrentLiabilities, idx, "liabilityType", e.target.value)} options={liabilityTypeOptions} />
										<TextField label={idx === 0 ? "Company name" : ""} value={row.holderName} onChange={(e) => setRowField(setCurrentLiabilities, idx, "holderName", e.target.value)} />
										<TextField label={idx === 0 ? "Account number" : ""} value={row.accountIdentifier} onChange={(e) => setRowField(setCurrentLiabilities, idx, "accountIdentifier", e.target.value)} />
										<TextField label={idx === 0 ? "Unpaid balance ($)" : ""} value={row.unpaidBalanceAmount} onChange={(e) => setRowField(setCurrentLiabilities, idx, "unpaidBalanceAmount", e.target.value)} />
										<TextField label={idx === 0 ? "Monthly amount ($)" : ""} value={row.monthlyPaymentAmount} onChange={(e) => setRowField(setCurrentLiabilities, idx, "monthlyPaymentAmount", e.target.value)} />
									</Box>
									{deleteRowButton("liabilities", idx)}
								</Stack>
								<Stack direction="row" gap={3} flexWrap="wrap" mb={1}>
									<CustomCheckbox checked={!!row.payoffStatusIndicator} handleChange={(e) => setRowField(setCurrentLiabilities, idx, "payoffStatusIndicator", e.target.checked)} label="Paid off" />
									<CustomCheckbox checked={!!row.reoId} handleChange={() => {}} label="Included in RE owned" disabled />
									<CustomCheckbox checked={!!row.exclusionIndicator} handleChange={(e) => setRowField(setCurrentLiabilities, idx, "exclusionIndicator", e.target.checked)} label="Exclude DTI" />
									<CustomCheckbox checked={!!row.subjectLoanResubordinationIndicator} handleChange={(e) => setRowField(setCurrentLiabilities, idx, "subjectLoanResubordinationIndicator", e.target.checked)} label="Resubordinate indicator" />
									<CustomCheckbox checked={!!row.bankLiabilityConsideredIndicator} handleChange={(e) => setRowField(setCurrentLiabilities, idx, "bankLiabilityConsideredIndicator", e.target.checked)} label="Bank liabilities considered in exposure" />
								</Stack>
								<Box className={styles.formGrid2} mb={2}>
									<SelectField label="Current lien position" value={row.currentLienPosition} onChange={(e) => setRowField(setCurrentLiabilities, idx, "currentLienPosition", e.target.value)} options={lienPositionOptions} error={assetErr(`liabilities[${idx}].currentLienPosition`)} required={!!(row.subjectLoanResubordinationIndicator || row.bankLiabilityConsideredIndicator)} />
									<SelectField label="Proposed lien position" value={row.proposedLienPosition} onChange={(e) => setRowField(setCurrentLiabilities, idx, "proposedLienPosition", e.target.value)} options={lienPositionOptions} error={assetErr(`liabilities[${idx}].proposedLienPosition`)} required={!!(row.subjectLoanResubordinationIndicator || row.bankLiabilityConsideredIndicator)} />
								</Box>
							</React.Fragment>
						))}
						<Box mb={3}>
							<SecondaryButton text="+ Add liability" onClick={() => handleAddAssetRow("liabilities")} />
						</Box>

						<span className={styles.sectionLabel}>Other liabilities</span>
						{assets.otherLiabilities.map((row, idx) => (
							<Stack key={row.id || idx} direction="row" gap={1} alignItems="flex-end">
								<Box className={styles.formGrid} sx={{ flex: 1 }}>
									<SelectField label={idx === 0 ? "Owner" : ""} value={row.borrowerType} onChange={(e) => setRowField(setCurrentOtherLiabilities, idx, "borrowerType", e.target.value)} options={ownerOptions} />
									<SelectField label={idx === 0 ? "Liability or expense type" : ""} value={row.liabilityOrExpenseType} onChange={(e) => setRowField(setCurrentOtherLiabilities, idx, "liabilityOrExpenseType", e.target.value)} options={otherLiabilitiesTypeOptions} />
									<TextField label={idx === 0 ? "Description" : ""} value={row.otherDescription} onChange={(e) => setRowField(setCurrentOtherLiabilities, idx, "otherDescription", e.target.value)} disabled={row.liabilityOrExpenseType !== "Other"} />
									<TextField label={idx === 0 ? "Monthly amount ($)" : ""} value={row.monthlyPayment} onChange={(e) => setRowField(setCurrentOtherLiabilities, idx, "monthlyPayment", e.target.value)} />
								</Box>
								{deleteRowButton("otherLiabilities", idx)}
							</Stack>
						))}
						<Box mb={3}>
							<SecondaryButton text="+ Add other liability" onClick={() => handleAddAssetRow("otherLiabilities")} />
						</Box>

						<span className={styles.sectionLabel}>Real estate owned</span>
						{assets.reoProperties.map((row, idx) => {
							const reoKey = reoLinkKey(row);
							const linkedCount = assets.liabilities.filter((l) => !l?.removed && l?.reoId && l.reoId === reoKey).length;
							return (
								<React.Fragment key={row.id || reoKey || idx}>
									<Stack direction="row" gap={1} alignItems="flex-end">
										<Box className={styles.formGrid} sx={{ flex: 1 }}>
											<SelectField label="Owner" value={row.owner} onChange={(e) => setRowField(setCurrentReoProperties, idx, "owner", e.target.value)} options={ownerOptions} />
											<SelectField label="Status" value={row.dispositionStatusType} onChange={(e) => setRowField(setCurrentReoProperties, idx, "dispositionStatusType", e.target.value)} options={realEstateStatusOptions} />
											<SelectField label="Property use" value={row.futurePropertyUsageType} onChange={(e) => setRowField(setCurrentReoProperties, idx, "futurePropertyUsageType", e.target.value)} options={realEstateUseOptions} error={assetErr(`reoProperties[${idx}].futurePropertyUsageType`)} required />
											<TextField label="Property value ($)" value={row.marketValueAmount} onChange={(e) => setRowField(setCurrentReoProperties, idx, "marketValueAmount", e.target.value)} />
											<TextField label="Monthly insurance, taxes, HOA ($)" value={row.maintenanceExpenseAmount} onChange={(e) => setRowField(setCurrentReoProperties, idx, "maintenanceExpenseAmount", e.target.value)} />
											<TextField label="Current or estimated monthly rent ($)" value={row.rentalIncomeGrossAmount} onChange={(e) => setRowField(setCurrentReoProperties, idx, "rentalIncomeGrossAmount", e.target.value)} />
										</Box>
										{deleteRowButton("reoProperties", idx)}
									</Stack>
									<Box mb={1}>
										<CustomCheckbox checked={!!row.subjectIndicator} handleChange={(e) => setRowField(setCurrentReoProperties, idx, "subjectIndicator", e.target.checked)} label="Subject property" />
									</Box>
									<Box className={styles.formGrid}>
										<TextField label="Address line 1" value={row.uRLA2020StreetAddress} onChange={(e) => setRowField(setCurrentReoProperties, idx, "uRLA2020StreetAddress", e.target.value)} />
										<TextField label="Address line 2 / unit number" value={row.unitNumber} onChange={(e) => setRowField(setCurrentReoProperties, idx, "unitNumber", e.target.value)} placeholder="Optional" />
										<TextField label="City" value={row.city} onChange={(e) => setRowField(setCurrentReoProperties, idx, "city", e.target.value)} />
										<SelectField label="State" value={row.state} onChange={(e) => setRowField(setCurrentReoProperties, idx, "state", e.target.value)} options={stateOptions} />
										<TextField label="Postal code" value={row.postalCode} onChange={(e) => setRowField(setCurrentReoProperties, idx, "postalCode", e.target.value)} />
									</Box>
									<Stack direction="row" gap={2} alignItems="center" mb={1}>
										<SecondaryButton
											text={linkReoIndex === idx ? "Done linking liabilities" : `Link liabilities (${linkedCount} linked)`}
											onClick={() => setLinkReoIndex(linkReoIndex === idx ? null : idx)}
											disabled={assets.liabilities.length === 0}
										/>
										{linkedCount > 0 && (
											<SecondaryButton
												text="View linked liabilities"
												onClick={() => setLinkedDrawerReo(row)}
											/>
										)}
									</Stack>
									{linkReoIndex === idx && (
										<Stack direction="column" gap={0.5} mb={2}>
												{/* Only liabilities that are unlinked or linked to this property. */}
											{assets.liabilities.map((liability, lIdx) =>
												liability.removed || (liability.reoId && liability.reoId !== reoKey) ? null : (
													<CustomCheckbox
														key={liability.id || lIdx}
														checked={!!reoKey && liability.reoId === reoKey}
														handleChange={() => toggleLiabilityLink(liability, lIdx, reoKey)}
														label={`${liability.holderName || "Liability"} ${liability.accountIdentifier ? `· ${liability.accountIdentifier}` : ""}`.trim()}
													/>
												)
											)}
										</Stack>
									)}
									<Divider sx={{ my: 2 }} />
								</React.Fragment>
							);
						})}
						<Box mb={3}>
							<SecondaryButton text="+ Add real estate owned" onClick={() => handleAddAssetRow("reoProperties")} />
						</Box>

						{assetsError && <RequiredError text={assetsError} />}
						<Box className={styles.saveRow}>
							<PrimaryButton text="Save assets →" onClick={handleSaveAssets} disabled={isSaving || auraLocked} />
						</Box>
					</QualifyAccordion>

					{/* ── 5 Declarations (Add New Loan Step 6) ──────────────────────── */}
					<QualifyAccordion
						stepLabel="5"
						title={`Declarations${pairs.length > 1 ? ` — Pair ${activePair + 1}` : ""}`}
						status={displaySectionStatus("declarations")}
						onInteract={markSectionInteraction("declarations")}
						isOpen={openSection === "declarations"}
						onToggle={() => toggle("declarations")}
					>
						<span className={styles.sectionLabel}>Borrower</span>
						{renderDeclarations(bDecl, setCurrentBDecl, "bDecl")}

						{hasCoBorrower && (
							<>
								<Divider sx={{ my: 2 }} />
								<span className={styles.sectionLabel}>Co-borrower</span>
								{renderDeclarations(cbDecl, setCurrentCbDecl, "cbDecl")}
							</>
						)}

						{declarationsError && <RequiredError text={declarationsError} />}
						<Box className={styles.saveRow}>
							<PrimaryButton text="Save declarations →" onClick={handleSaveDeclarations} disabled={isSaving || auraLocked} />
						</Box>
					</QualifyAccordion>

					{/* ── 6 Closing (Add New Loan Step 10) ──────────────────────────── */}
					<QualifyAccordion
						stepLabel="6"
						title="Closing information"
						status={displaySectionStatus("closing")}
						onInteract={markSectionInteraction("closing")}
						isOpen={openSection === "closing"}
						onToggle={() => toggle("closing")}
					>
						{isCbWhs && (
							<>
								<span className={styles.sectionLabel}>Loan type</span>
								<Box className={styles.formGrid}>
									<TextField label="Closing date" value={closing.closingDate} onChange={upd(setClosing, "closingDate")} placeholder="MM/DD/YYYY" error={shownClosingFieldErrors.has("closing.closingDate")} required />
									{closingContext.loanPurpose === "Purchase" && (
										<TextField label="Purchase contract close date" value={closing.purchaseContractDate} onChange={upd(setClosing, "purchaseContractDate")} placeholder="MM/DD/YYYY" error={shownClosingFieldErrors.has("closing.purchaseContractDate")} />
									)}
								</Box>

								<span className={styles.sectionLabel}>Fee reimbursement</span>
								{[
									["Credit report", "creditReportFee", "creditReportProvider"],
									["Appraisal fee", "appraisalFee", "appraisalProvider"],
									["Second appraisal fee", "secondAppraisalFee", "secondAppraisalProvider"],
									["Processing fee", "processingFee", "processingProvider"],
									["Third party processing fee", "thirdPartyFee", "thirdPartyProvider"],
								].map(([label, feeKey, provKey]) => (
									<Box key={feeKey} className={styles.formGrid2}>
										<TextField label={`${label} — fees ($)`} value={closing[feeKey]} onChange={upd(setClosing, feeKey)} />
										<TextField label={`${label} — service provider`} value={closing[provKey]} onChange={upd(setClosing, provKey)} placeholder="Provider name" />
									</Box>
								))}
							</>
						)}

						<span className={styles.sectionLabel}>Vesting</span>
						<Box className={styles.formGrid2}>
							<SelectField label="How will the transaction be vested?" value={closing.vestingType} onChange={upd(setClosing, "vestingType")} options={vestingMethodOptions} error={shownClosingFieldErrors.has("closing.vestingType")} required />
							<TextField label="Vesting fully written as" value={closing.vestingWrittenAs} onChange={upd(setClosing, "vestingWrittenAs")} placeholder="Full legal vesting statement" />
						</Box>

						{isCbWhs && (
							<>
								<span className={styles.sectionLabel} style={{ marginTop: 16, display: "block" }}>Compensation</span>
								<Box className={styles.formGrid}>
									<SelectField label="LPC percentage" value={closing.lpcPercent} onChange={() => {}} options={lpcValueOptions} disabled />
									<TextField label="LPC dollars ($)" value={closing.lpcDollars} onChange={() => {}} disabled />
									<SelectField label="BPC in percentage" value={closing.bpcPercent} onChange={upd(setClosing, "bpcPercent")} options={bpcValueOptions} error={shownClosingFieldErrors.has("closing.bpcPercent")} required={closingContext.compensationPlan === "Borrower Paid"} disabled={closingContext.compensationPlan !== "Borrower Paid"} />
									<TextField label="BPC in dollars ($)" value={closing.bpcDollars} onChange={upd(setClosing, "bpcDollars")} error={shownClosingFieldErrors.has("closing.bpcDollars")} required={closingContext.compensationPlan === "Borrower Paid"} disabled={closingContext.compensationPlan !== "Borrower Paid"} />
								</Box>

								<span className={styles.sectionLabel}>Escrow</span>
								<Box className={styles.formGrid}>
									<TextField label="Company name" value={closing.escrowCompanyName} onChange={upd(setClosing, "escrowCompanyName")} />
									<TextField label="Escrow company address" value={closing.escrowStreet} onChange={upd(setClosing, "escrowStreet")} />
									<TextField label="City" value={closing.escrowCity} onChange={upd(setClosing, "escrowCity")} />
									<SelectField label="State" value={closing.escrowState} onChange={upd(setClosing, "escrowState")} options={stateOptions} />
									<TextField label="Zipcode" value={closing.escrowZip} onChange={upd(setClosing, "escrowZip")} />
								</Box>
								<Box className={styles.formGrid}>
									<TextField label="Escrow officer name" value={closing.escrowOfficerName} onChange={upd(setClosing, "escrowOfficerName")} />
									<PhoneField label="Escrow officer cell phone number" value={closing.escrowOfficerPhone} onChange={updv(setClosing, "escrowOfficerPhone")} />
									<TextField label="Escrow officer email ID" value={closing.escrowOfficerEmail} onChange={upd(setClosing, "escrowOfficerEmail")} />
								</Box>
							</>
						)}

						<span className={styles.sectionLabel}>Power of attorney</span>
						<Box className={styles.formGrid}>
							<SelectField label="Will POA be used for borrower?" value={closing.poaBorrower} onChange={upd(setClosing, "poaBorrower")} options={YNOptions} error={shownClosingFieldErrors.has("closing.poaBorrower")} required />
							{closingContext.coBorrowerName && (
								<SelectField label="Will POA be used for co-borrower?" value={closing.poaCoBorrower} onChange={upd(setClosing, "poaCoBorrower")} options={YNOptions} error={shownClosingFieldErrors.has("closing.poaCoBorrower")} required />
							)}
						</Box>

						{closingContext.occupancy === "Investor" && (
							<>
								<span className={styles.sectionLabel}>Business purpose</span>
								<Box className={styles.formGrid}>
									<SelectField label="Cash out proceeds being utilized for business purposes" value={closing.cashOutBusinessPurpose} onChange={upd(setClosing, "cashOutBusinessPurpose")} options={YNOptions} error={shownClosingFieldErrors.has("closing.cashOutBusinessPurpose")} required disabled={cashOutLocked} />
									<SelectField label="Will the property be occupied by a family member?" value={closing.occupiedByFamily} onChange={upd(setClosing, "occupiedByFamily")} options={YNOptions} error={shownClosingFieldErrors.has("closing.occupiedByFamily")} required disabled={occupiedByFamilyLocked} />
								</Box>
							</>
						)}

						{closingError && <RequiredError text={closingError} />}
						<Box className={styles.saveRow}>
							<PrimaryButton text="Save closing info →" onClick={handleSaveClosing} disabled={isSaving || auraLocked} />
						</Box>
					</QualifyAccordion>
				</Stack>

				{continueError && <RequiredError text={continueError} />}
				<Stack
					direction="row"
					alignItems="center"
					justifyContent="flex-end"
					gap={1.5}
					className={styles.actionBar}
				>
					<SecondaryButton text="Save and close" onClick={handleSaveAndClose} disabled={isSaving} />
					<PrimaryButton text="Save and continue →" onClick={handleContinue} disabled={isSaving || auraLocked} />
				</Stack>

				<Box className={styles.disclaimer}>
					Not a commitment to lend. All loans subject to underwriting approval.
				</Box>
			</Box>
		</CustomLayout>
	);
};

export default DashboardPage;
